import ReviewFileInput from '../shared/ReviewFileInput';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FaArrowDown, FaArrowUp, FaBars, FaBold, FaBorderAll, FaCircle,
  FaCompress, FaCopy, FaDownload, FaEraser, FaExpand, FaFont,
  FaGripLines, FaHighlighter, FaIcons, FaImage, FaItalic,
  FaLongArrowAltRight, FaMinus, FaMousePointer, FaPen, FaPencilAlt,
  FaPlus, FaRedo, FaRegSquare, FaSave, FaShapes, FaSlash,
  FaStickyNote, FaTimes, FaTrash, FaUnderline, FaUndo,
  FaWindowMinimize,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import SeminaryLanguageKeyboard from './SeminaryLanguageKeyboard';
import {
  appendStrokeSamples,
  buildPressureStrokePaths,
  getPointerReleaseSamples,
  buildSmoothStrokePath,
  getBoardSampleSpacing,
  getCoalescedPointerEvents,
  normalizePointerPressure,
  projectPointerToBoard,
  snapshotPointerSamples,
} from '../../utils/blackboardStroke';
import { subscribeBlackboardScriptureInsert } from '../../utils/blackboardScriptureBridge';
import { canStudentRepositionSharedBoard } from '../../utils/meetingAuthorization';

import { getUserId } from '../../utils/api';
import { registerClassBoardFlush, readClassBoardDraft, writeClassBoardDraft, clearClassBoardDraft } from '../../utils/classBoardPersistence';
import { handwritingSelection, renderHandwriting } from '../../utils/blackboardHandwriting';

const BOARD_WIDTH = 1600;
const BOARD_HEIGHT = 900;
const MAX_HISTORY = 60;
const ICONS = ['⭐', '✅', '❌', '⚠️', '❓', '💡', '📖', '✝️', '🙏', '❤️', '👍', '👎', '➕', '➖', '➡️', '⬅️'];
const BACKGROUNDS = ['#ffffff', '#f8fafc', '#fff7ed', '#fefce8', '#ecfeff', '#eff6ff', '#f5f3ff', '#111827', '#0f3d2e'];
const ORIGINAL_LANGUAGE_FONT = '"Noto Serif Hebrew", "Noto Sans Hebrew", "Noto Serif Greek", "Noto Sans Greek", "SBL Hebrew", "Ezra SIL", "Gentium Plus", "Times New Roman", serif';
const FONT_OPTIONS = Object.freeze([
  { label: 'Original languages (Greek / Hebrew)', value: ORIGINAL_LANGUAGE_FONT },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Courier New', value: '"Courier New", monospace' },
]);
const HEBREW_TEXT_RE = /[֐-׿]/;
const GREEK_TEXT_RE = /[Ͱ-Ͽἀ-῿]/;
const normalizeRawBoardText = (value) => {
  const normalized = String(value || '').replace(/\r\n?/g, '\n');
  return typeof normalized.normalize === 'function' ? normalized.normalize('NFC') : normalized;
};
const normalizeBoardText = (value, maximumLength = 20000) => {
  return normalizeRawBoardText(value).slice(0, maximumLength);
};
const normalizeElementText = (value, elementType = 'text') => normalizeBoardText(
  value,
  elementType === 'sticky' ? 10000 : 20000
);
const getFirstStrongScript = (value) => String(value || '').match(/[A-Za-z\u0370-\u03ff\u1f00-\u1fff\u0590-\u05ff]/u)?.[0] || '';
const getTextDirection = (value) => HEBREW_TEXT_RE.test(getFirstStrongScript(value)) ? 'rtl' : 'ltr';
const getTextLanguage = (value) => {
  const firstStrongScript = getFirstStrongScript(value);
  if (HEBREW_TEXT_RE.test(firstStrongScript)) return 'he';
  if (GREEK_TEXT_RE.test(firstStrongScript)) return 'grc';
  return 'en';
};
const splitScriptureTextForBoard = ({ payload, fallbackText, mode }) => {
  const storageLimit = mode === 'sticky' ? 10000 : 20000;
  // Long passages are split into readable board-sized cards instead of one
  // valid but visually unusable 10k/20k element. This also protects an
  // unusually long verse, while every part repeats source rights.
  const readableLimit = mode === 'sticky' ? 900 : 1600;
  const translations = Array.isArray(payload.translations) ? payload.translations : [];
  const sourceLines = translations.map((translation) => {
    const label = translation.shortName || translation.name || translation.id || 'Bible source';
    const rights = [translation.attribution, translation.license].filter(Boolean).join(' · ');
    return rights ? `${label} — ${rights}` : String(label);
  });
  const sourceFooter = `Sources / licences:\n${sourceLines.join('\n')}`;
  const reference = String(payload.reference || 'Scripture').slice(0, 200);
  // Source and licence attribution is protected space. If unusually verbose
  // source metadata needs more than the visual target, grow the part only as
  // far as the server's persisted element limit instead of truncating rights.
  const maximumLength = Math.min(
    storageLimit,
    Math.max(readableLimit, sourceFooter.length + reference.length + 320)
  );
  const rawText = normalizeRawBoardText(payload.text || fallbackText);
  if (rawText.length <= maximumLength) return [rawText];
  const bodyLimit = Math.max(
    120,
    maximumLength - sourceFooter.length - reference.length - 120
  );
  const bodyChunks = [];
  let current = '';

  const pushLine = (line) => {
    const candidate = current ? `${current}\n${line}` : line;
    if (candidate.length <= bodyLimit) {
      current = candidate;
      return;
    }
    if (current) bodyChunks.push(current);
    current = '';
    const codePoints = Array.from(line);
    for (let start = 0; start < codePoints.length; start += bodyLimit) {
      const segment = codePoints.slice(start, start + bodyLimit).join('');
      if (segment.length >= bodyLimit || start + bodyLimit < codePoints.length) {
        bodyChunks.push(segment);
      } else {
        current = segment;
      }
    }
  };
  rawText.split('\n').forEach(pushLine);
  if (current) bodyChunks.push(current);

  const partCount = Math.max(1, bodyChunks.length);
  return bodyChunks.map((body, index) => normalizeBoardText([
    `${reference} · Part ${index + 1}/${partCount}`,
    body,
    sourceFooter,
  ].join('\n\n'), maximumLength));
};
const SIZE_CLASSES = {
  small: 'h-[70vh] w-[94vw] max-w-[900px]',
  medium: 'h-[84vh] w-[97vw] max-w-[1280px]',
  large: 'h-[95vh] w-[99vw]',
  // `h-full` deliberately respects the reserved meeting-control area. Using
  // `h-screen` here placed the board over the controls on smaller devices.
  full: 'h-full w-full rounded-none',
};

const makeId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
const emptyPage = (number = 1) => ({
  pageId: makeId(),
  name: `Page ${number}`,
  background: '#ffffff',
  backgroundStyle: 'plain',
  elements: [],
});
const deepClone = (value) => JSON.parse(JSON.stringify(value));
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const pathData = (points = []) => buildSmoothStrokePath(points);
const pointerPoint = (event, svg) => projectPointerToBoard(
  event?.nativeEvent || event,
  svg,
  BOARD_WIDTH,
  BOARD_HEIGHT
);
const safePages = (value) => Array.isArray(value) && value.length ? value : [emptyPage()];

const boundsOf = (element) => {
  if (!element) return null;
  if (element.type === 'path') {
    const points = element.points || [];
    if (!points.length) return null;
    const xs = points.map((item) => item.x);
    const ys = points.map((item) => item.y);
    return { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(10, Math.max(...xs) - Math.min(...xs)), height: Math.max(10, Math.max(...ys) - Math.min(...ys)) };
  }
  if (['line', 'arrow', 'triangle'].includes(element.type)) {
    return { x: Math.min(element.x1, element.x2), y: Math.min(element.y1, element.y2), width: Math.max(10, Math.abs(element.x2 - element.x1)), height: Math.max(10, Math.abs(element.y2 - element.y1)) };
  }
  if (element.type === 'ellipse') return { x: element.cx - element.rx, y: element.cy - element.ry, width: element.rx * 2, height: element.ry * 2 };
  if (element.type === 'text') {
    const fontSize = Number(element.fontSize) || 44;
    const width = clamp(Number(element.width) || 720, 180, BOARD_WIDTH);
    const lines = wrapText(
      element.text,
      Math.max(6, Math.floor(width / (fontSize * 0.58)))
    );
    const direction = element.direction || getTextDirection(element.text);
    return {
      x: direction === 'rtl' ? element.x - width : element.x,
      y: element.y - fontSize,
      width,
      height: Math.max(
        Number(element.height) || 0,
        fontSize * 1.35,
        lines.length * fontSize * 1.2
      ),
    };
  }
  if (element.type === 'icon') return { x: element.x, y: element.y - element.size, width: element.size, height: element.size };
  return { x: element.x, y: element.y, width: element.width, height: element.height };
};

const translateElement = (element, dx, dy) => {
  if (element.locked) return element;
  if (element.type === 'path') return { ...element, points: element.points.map((item) => ({ ...item, x: clamp(item.x + dx, 0, BOARD_WIDTH), y: clamp(item.y + dy, 0, BOARD_HEIGHT) })) };
  if (['line', 'arrow', 'triangle'].includes(element.type)) return { ...element, x1: element.x1 + dx, y1: element.y1 + dy, x2: element.x2 + dx, y2: element.y2 + dy };
  if (element.type === 'ellipse') return { ...element, cx: element.cx + dx, cy: element.cy + dy };
  const moved = { ...element, x: element.x + dx, y: element.y + dy };
  const bounds = boundsOf(moved);
  if (!bounds) return moved;
  const correctionX = bounds.x < 0
    ? -bounds.x
    : bounds.x + bounds.width > BOARD_WIDTH
      ? BOARD_WIDTH - (bounds.x + bounds.width)
      : 0;
  const correctionY = bounds.y < 0
    ? -bounds.y
    : bounds.y + bounds.height > BOARD_HEIGHT
      ? BOARD_HEIGHT - (bounds.y + bounds.height)
      : 0;
  return {
    ...moved,
    x: moved.x + correctionX,
    y: moved.y + correctionY,
  };
};

const wrapText = (value, maxCharacters = 28) => {
  const lines = [];
  String(value || '').split('\n').forEach((paragraph) => {
    let remaining = Array.from(paragraph.trim());
    if (!remaining.length) { lines.push(''); return; }
    while (remaining.length > maxCharacters) {
      let splitAt = maxCharacters;
      for (let index = maxCharacters; index > 0; index -= 1) {
        if (/\s/u.test(remaining[index])) {
          splitAt = index;
          break;
        }
      }
      lines.push(remaining.slice(0, splitAt).join('').trimEnd());
      remaining = remaining.slice(splitAt);
      while (remaining.length && /\s/u.test(remaining[0])) remaining.shift();
    }
    lines.push(remaining.join(''));
  });
  return lines;
};

const resizeElement = (element, dx, dy) => {
  if (!element || element.locked || !['text', 'sticky'].includes(element.type)) return element;
  const direction = element.direction || getTextDirection(element.text);
  const widthDelta = element.type === 'text' && direction === 'rtl' ? -dx : dx;
  return translateElement({
    ...element,
    width: clamp(Number(element.width || (element.type === 'sticky' ? 620 : 720)) + widthDelta, 180, 1400),
    height: clamp(Number(element.height || (element.type === 'sticky' ? 480 : 220)) + dy, 120, 820),
  }, 0, 0);
};

export const BackgroundLayer = ({ page }) => {
  const patternId = `board-pattern-${String(page?.pageId || 'page').replace(/[^a-z0-9_-]/gi, '')}`;
  const dark = ['#111827', '#0f3d2e'].includes(page?.background);
  const line = dark ? '#ffffff22' : '#0f172a18';
  return <>
    <defs>
      <pattern id={patternId} width="40" height="40" patternUnits="userSpaceOnUse">
        {page?.backgroundStyle === 'grid' && <path d="M 40 0 L 0 0 0 40" fill="none" stroke={line} strokeWidth="1" />}
        {page?.backgroundStyle === 'lines' && <path d="M 0 39 L 40 39" fill="none" stroke={line} strokeWidth="1.4" />}
        {page?.backgroundStyle === 'dots' && <circle cx="4" cy="4" r="2" fill={line} />}
        {page?.backgroundStyle === 'music' && <>
          {[8, 16, 24, 32, 40].map((y) => <path key={y} d={`M 0 ${y} L 40 ${y}`} stroke={line} strokeWidth="1" />)}
        </>}
      </pattern>
      <marker id="board-arrow-head" markerWidth="12" markerHeight="12" refX="10" refY="6" orient="auto" markerUnits="strokeWidth">
        <path d="M 0 0 L 12 6 L 0 12 z" fill="context-stroke" />
      </marker>
    </defs>
    <rect width={BOARD_WIDTH} height={BOARD_HEIGHT} fill={page?.background || '#ffffff'} />
    {page?.backgroundStyle !== 'plain' && <rect width={BOARD_WIDTH} height={BOARD_HEIGHT} fill={`url(#${patternId})`} />}
  </>;
};

export const BoardElement = ({ element, selected }) => {
  const common = {
    'data-element-id': element.id,
    opacity: element.opacity ?? 1,
    style: { cursor: 'inherit' },
  };
  let node = null;

  if (element.type === 'path') {
    const pressureSegments = element.pressureSensitive
      ? buildPressureStrokePaths(element.points, element.strokeWidth)
      : [];
    node = pressureSegments.length ? (
      <g {...common}>
        {pressureSegments.map((segment) => (
          <path
            key={segment.key}
            data-element-id={element.id}
            d={segment.d}
            fill="none"
            stroke={element.stroke}
            strokeWidth={segment.strokeWidth}
            strokeDasharray={(element.dash || []).join(' ')}
            strokeLinecap={element.lineCap || 'round'}
            strokeLinejoin="round"
          />
        ))}
      </g>
    ) : (
      <path
        {...common}
        d={pathData(element.points)}
        fill="none"
        stroke={element.stroke}
        strokeWidth={element.strokeWidth}
        strokeDasharray={(element.dash || []).join(' ')}
        strokeLinecap={element.lineCap || 'round'}
        strokeLinejoin="round"
      />
    );
  }
  if (element.type === 'line' || element.type === 'arrow') {
    node = (
      <line
        {...common}
        x1={element.x1}
        y1={element.y1}
        x2={element.x2}
        y2={element.y2}
        stroke={element.stroke}
        strokeWidth={element.strokeWidth}
        strokeDasharray={(element.dash || []).join(' ')}
        strokeLinecap="round"
        markerEnd={element.type === 'arrow' ? 'url(#board-arrow-head)' : undefined}
      />
    );
  }
  if (element.type === 'rect') {
    node = (
      <rect
        {...common}
        x={element.x}
        y={element.y}
        width={element.width}
        height={element.height}
        rx={element.rx || 0}
        fill={element.fill || 'transparent'}
        stroke={element.stroke}
        strokeWidth={element.strokeWidth}
      />
    );
  }
  if (element.type === 'ellipse') {
    node = (
      <ellipse
        {...common}
        cx={element.cx}
        cy={element.cy}
        rx={element.rx}
        ry={element.ry}
        fill={element.fill || 'transparent'}
        stroke={element.stroke}
        strokeWidth={element.strokeWidth}
      />
    );
  }
  if (element.type === 'triangle') {
    node = (
      <polygon
        {...common}
        points={`${element.x1},${element.y2} ${(element.x1 + element.x2) / 2},${element.y1} ${element.x2},${element.y2}`}
        fill={element.fill || 'transparent'}
        stroke={element.stroke}
        strokeWidth={element.strokeWidth}
        strokeLinejoin="round"
      />
    );
  }
  if (element.type === 'text') {
    const direction = element.direction || getTextDirection(element.text);
    const language = element.language || getTextLanguage(element.text);
    const anchor = direction === 'rtl' ? 'end' : 'start';
    const fontSize = Number(element.fontSize) || 44;
    const width = clamp(Number(element.width) || 720, 180, BOARD_WIDTH);
    const wrappedLines = wrapText(
      element.text,
      Math.max(6, Math.floor(width / (fontSize * 0.58)))
    );
    node = (
      <text
        {...common}
        x={element.x}
        y={element.y}
        fill={element.colour}
        fontSize={fontSize}
        fontFamily={element.fontFamily || ORIGINAL_LANGUAGE_FONT}
        fontWeight={element.fontWeight}
        fontStyle={element.fontStyle}
        textDecoration={element.textDecoration}
        textAnchor={anchor}
        direction={direction}
        lang={language}
        data-board-wrapped-text="true"
        style={{ ...common.style, unicodeBidi: 'plaintext' }}
      >
        {wrappedLines.map((line, index) => (
          <tspan
            key={index}
            x={element.x}
            dy={index ? fontSize * 1.2 : 0}
            direction={direction}
          >
            {line || '\u00a0'}
          </tspan>
        ))}
      </text>
    );
  }
  if (element.type === 'sticky') {
    const direction = element.direction || getTextDirection(element.text);
    const language = element.language || getTextLanguage(element.text);
    const fontSize = Number(element.fontSize) || 40;
    const textX = direction === 'rtl'
      ? element.x + element.width - 22
      : element.x + 22;
    const anchor = direction === 'rtl' ? 'end' : 'start';
    node = (
      <g {...common}>
        <rect
          data-element-id={element.id}
          x={element.x}
          y={element.y}
          width={element.width}
          height={element.height}
          rx="18"
          fill={element.fill}
          stroke="#00000022"
          strokeWidth="3"
        />
        {wrapText(
          element.text,
          Math.max(12, Math.floor(element.width / (fontSize * 0.55)))
        ).map((line, index) => (
          <text
            data-element-id={element.id}
            key={index}
            x={textX}
            y={element.y + 42 + index * fontSize * 1.18}
            fill={element.colour}
            fontSize={fontSize}
            fontFamily={element.fontFamily || ORIGINAL_LANGUAGE_FONT}
            fontWeight={element.fontWeight}
            fontStyle={element.fontStyle}
            textDecoration={element.textDecoration}
            textAnchor={anchor}
            direction={direction}
            lang={language}
            style={{ unicodeBidi: 'plaintext' }}
          >
            {line || '\u00a0'}
          </text>
        ))}
      </g>
    );
  }
  if (element.type === 'icon') {
    node = (
      <text {...common} x={element.x} y={element.y} fontSize={element.size}>
        {element.icon}
      </text>
    );
  }
  if (element.type === 'image') {
    node = (
      <image
        {...common}
        href={element.src}
        x={element.x}
        y={element.y}
        width={element.width}
        height={element.height}
        preserveAspectRatio="xMidYMid meet"
      />
    );
  }

  const bounds = selected ? boundsOf(element) : null;
  return (
    <g>
      {node}
      {bounds && (
        <>
          <rect
            x={bounds.x - 8}
            y={bounds.y - 8}
            width={bounds.width + 16}
            height={bounds.height + 16}
            fill="none"
            stroke="#ec4899"
            strokeWidth="4"
            strokeDasharray="12 8"
            pointerEvents="none"
          />
          {['text', 'sticky'].includes(element.type) && !element.locked && (
            <circle
              data-element-id={element.id}
              data-board-resize-handle="true"
              aria-label="Resize selected board object"
              cx={
                element.type === 'text' &&
                (element.direction || getTextDirection(element.text)) === 'rtl'
                  ? bounds.x - 8
                  : bounds.x + bounds.width + 8
              }
              cy={bounds.y + bounds.height + 8}
              r="15"
              fill="#ec4899"
              stroke="#ffffff"
              strokeWidth="4"
              style={{ cursor: 'nwse-resize', pointerEvents: 'auto' }}
            />
          )}
        </>
      )}
    </g>
  );
};

const EphemeralPointerLayer = ({ pointers, pageId }) => (
  <g pointerEvents="none" aria-hidden="true" data-board-ephemeral-layer="true">
    {Object.entries(pointers || {}).map(([key, pointer]) => {
      if (pointer.active === false || String(pointer.pageId || '') !== String(pageId || '')) {
        return null;
      }
      const x = clamp(Number(pointer.x) || 0, 0, BOARD_WIDTH);
      const y = clamp(Number(pointer.y) || 0, 0, BOARD_HEIGHT);
      const colour = pointer.colour || participantColour(key);
      if (pointer.kind === 'laser') {
        return (
          <g key={key} data-board-laser-pointer="true">
            <circle cx={x} cy={y} r="23" fill="none" stroke={colour} strokeWidth="7" opacity="0.45" />
            <circle cx={x} cy={y} r="9" fill={colour} stroke="#ffffff" strokeWidth="3" />
          </g>
        );
      }
      return (
        <g key={key} transform={`translate(${x} ${y})`} data-board-collaborator-cursor="true">
          <path d="M 0 0 L 8 34 L 17 20 L 34 20 Z" fill={colour} stroke="#ffffff" strokeWidth="3" />
          <rect x="17" y="22" width={Math.max(88, String(pointer.name || 'Participant').length * 12)} height="34" rx="10" fill={colour} />
          <text x="28" y="46" fill="#ffffff" fontSize="20" fontFamily="Arial, sans-serif">
            {String(pointer.name || 'Participant').slice(0, 32)}
          </text>
        </g>
      );
    })}
  </g>
);

const MemoBoardElement = React.memo(BoardElement);

const ToolButton = ({ active, title, onClick, children, disabled = false }) => <button
  type="button" title={title} onClick={onClick} disabled={disabled}
  className={`flex min-h-10 items-center justify-center gap-2 rounded-lg border px-2 py-2 text-sm transition ${active ? 'border-pink-400 bg-pink-700 text-white' : 'border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700'} disabled:cursor-not-allowed disabled:opacity-40`}
>{children}</button>;

const InlineTextEditor = ({ editor, inputRef, onChange, onMove, onFinish, onActivate }) => {
  const editorDragRef = useRef(null);
  if (!editor) return null;
  const value = normalizeBoardText(editor.value);
  const direction = getTextDirection(value);
  const sticky = editor.elementType === 'sticky';
  const editorWidth = sticky
    ? clamp(Number(editor.width) || 340, 240, 720)
    : clamp(Number(editor.width) || Math.max(520, (Number(editor.fontSize) || 44) * 12), 300, 1000);
  const lineCount = Math.max(1, value.split('\n').length);
  const editorHeight = sticky
    ? clamp(Number(editor.height) || 210, 180, 520)
    : clamp(
      Math.max(Number(editor.height) || 220, lineCount * (Number(editor.fontSize) || 44) * 1.25 + 116),
      220,
      650
    );
  const anchoredX = direction === 'rtl' && !sticky
    ? Number(editor.x) - editorWidth
    : Number(editor.x);
  const editorX = clamp(anchoredX, 0, BOARD_WIDTH - editorWidth);
  const editorY = clamp(
    sticky ? Number(editor.y) : Number(editor.y) - (Number(editor.fontSize) || 44) * 1.2,
    0,
    BOARD_HEIGHT - editorHeight
  );
  const stopPointer = (event) => event.stopPropagation();
  const finishFromPointer = (mode, event) => {
    event.preventDefault();
    event.stopPropagation();
    onFinish(mode);
  };
  const beginEditorDrag = (event) => {
    const foreignObject = event.currentTarget.closest?.('foreignObject');
    const svg = foreignObject?.ownerSVGElement || foreignObject?.closest?.('svg');
    const rect = svg?.getBoundingClientRect?.();
    if (!rect?.width || !rect?.height) return;
    editorDragRef.current = {
      pointerId: event.pointerId,
      clientX: event.clientX,
      clientY: event.clientY,
      displayX: editorX,
      displayY: editorY,
      scaleX: BOARD_WIDTH / rect.width,
      scaleY: BOARD_HEIGHT / rect.height,
    };
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}
    event.preventDefault();
    event.stopPropagation();
    onActivate?.();
  };
  const moveEditorDrag = (event) => {
    const drag = editorDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const displayX = clamp(
      drag.displayX + (event.clientX - drag.clientX) * drag.scaleX,
      0,
      BOARD_WIDTH - editorWidth
    );
    const displayY = clamp(
      drag.displayY + (event.clientY - drag.clientY) * drag.scaleY,
      0,
      BOARD_HEIGHT - editorHeight
    );
    onMove?.({
      x: sticky ? displayX : direction === 'rtl' ? displayX + editorWidth : displayX,
      y: sticky ? displayY : displayY + (Number(editor.fontSize) || 44) * 1.2,
    });
    event.preventDefault();
    event.stopPropagation();
  };
  const endEditorDrag = (event) => {
    const drag = editorDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    editorDragRef.current = null;
    try { event.currentTarget.releasePointerCapture?.(event.pointerId); } catch {}
    event.preventDefault();
    event.stopPropagation();
    window.requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <foreignObject
      x={editorX}
      y={editorY}
      width={editorWidth}
      height={editorHeight}
      data-board-inline-editor="true"
      onPointerDown={stopPointer}
      onPointerMove={stopPointer}
      onPointerUp={stopPointer}
      onPointerCancel={stopPointer}
      onDoubleClick={stopPointer}
    >
      <div
        xmlns="http://www.w3.org/1999/xhtml"
        style={{
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          border: '5px solid #ec4899',
          borderRadius: 16,
          background: sticky ? editor.fill || '#fef08a' : 'rgba(255,255,255,0.98)',
          boxShadow: '0 16px 40px rgba(15,23,42,0.38)',
          pointerEvents: 'auto',
        }}
      >
        <div
          role="button"
          tabIndex="0"
          aria-label="Drag inline text editor to board position"
          onPointerDown={beginEditorDrag}
          onPointerMove={moveEditorDrag}
          onPointerUp={endEditorDrag}
          onPointerCancel={endEditorDrag}
          onLostPointerCapture={endEditorDrag}
          style={{
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            minHeight: 42,
            padding: '7px 12px',
            background: '#be185d',
            color: '#ffffff',
            cursor: 'move',
            touchAction: 'none',
            direction: 'ltr',
            fontFamily: 'Arial, sans-serif',
            fontSize: 16,
            fontWeight: 700,
            userSelect: 'none',
          }}
        >
          Drag to position · then type and Apply
        </div>
        <textarea
          ref={inputRef}
          data-testid="blackboard-inline-text-editor"
          aria-label={editor.elementId ? 'Edit blackboard text' : 'Add blackboard text'}
          value={value}
          dir={direction}
          lang={getTextLanguage(value)}
          spellCheck={false}
          inputMode="text"
          onFocus={() => onActivate?.()}
          onSelect={() => onActivate?.()}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => onFinish('commit')}
          onKeyDown={(event) => {
            if (event.nativeEvent?.isComposing) return;
            if (event.key === 'Escape') {
              event.preventDefault();
              event.stopPropagation();
              onFinish('cancel');
              return;
            }
            if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
              event.preventDefault();
              event.stopPropagation();
              onFinish('commit');
            }
          }}
          style={{
            boxSizing: 'border-box',
            flex: '1 1 auto',
            minHeight: 0,
            width: '100%',
            resize: 'none',
            border: 0,
            outline: 0,
            padding: '18px 20px 10px',
            background: 'transparent',
            color: editor.colour || '#111827',
            fontFamily: editor.fontFamily || ORIGINAL_LANGUAGE_FONT,
            fontSize: Number(editor.fontSize) || 44,
            fontWeight: editor.fontWeight || 'normal',
            fontStyle: editor.fontStyle || 'normal',
            textDecoration: editor.textDecoration || 'none',
            lineHeight: 1.2,
            unicodeBidi: 'plaintext',
            userSelect: 'text',
            touchAction: 'manipulation',
          }}
        />
        <div
          style={{
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            minHeight: 54,
            padding: '7px 10px',
            background: 'rgba(15,23,42,0.94)',
            color: '#e2e8f0',
            direction: 'ltr',
            fontFamily: 'Arial, sans-serif',
            fontSize: 18,
          }}
        >
          <span style={{ flex: '1 1 auto' }}>
            Enter = new line · Ctrl/Cmd+Enter = apply · Esc = cancel
          </span>
          <button
            type="button"
            onPointerDown={(event) => finishFromPointer('cancel', event)}
            onClick={() => onFinish('cancel')}
            style={{ border: 0, borderRadius: 8, padding: '7px 12px', background: '#475569', color: '#fff' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onPointerDown={(event) => finishFromPointer('commit', event)}
            onClick={() => onFinish('commit')}
            style={{ border: 0, borderRadius: 8, padding: '7px 12px', background: '#be185d', color: '#fff', fontWeight: 700 }}
          >
            Apply
          </button>
        </div>
      </div>
    </foreignObject>
  );
};

const compressImage = (file) => new Promise((resolve, reject) => {
  if (!file?.type?.startsWith('image/')) return reject(new Error('Please choose an image file'));
  if (file.size > 12 * 1024 * 1024) return reject(new Error('Original image must be below 12 MB'));
  const reader = new FileReader();
  reader.onerror = () => reject(new Error('Unable to read image'));
  reader.onload = () => {
    const image = new Image();
    image.onerror = () => reject(new Error('Invalid image'));
    image.onload = () => {
      const scale = Math.min(1, 1280 / image.width, 720 / image.height);
      const width = Math.max(1, Math.round(image.width * scale));
      const height = Math.max(1, Math.round(image.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const context = canvas.getContext('2d');
      context.fillStyle = '#ffffff'; context.fillRect(0, 0, width, height);
      context.drawImage(image, 0, 0, width, height);
      let src = canvas.toDataURL('image/jpeg', 0.72);
      if (src.length > 480_000) src = canvas.toDataURL('image/jpeg', 0.48);
      if (src.length > 500_000) return reject(new Error('Compressed image is still too large; choose a smaller image'));
      resolve({ src, width, height });
    };
    image.src = reader.result;
  };
  reader.readAsDataURL(file);
});

const normalizedRole = (value) => String(value || '').trim().toLowerCase();
const participantColour = (value) => {
  const palette = ['#f43f5e', '#f59e0b', '#10b981', '#06b6d4', '#6366f1', '#a855f7'];
  const seed = Array.from(String(value || 'participant')).reduce(
    (sum, character) => sum + character.codePointAt(0),
    0
  );
  return palette[seed % palette.length];
};
const normalizeCollaborationState = (value = {}) => {
  const rawFocus = value.presenterFocus;
  const focus = rawFocus && typeof rawFocus === 'object'
    ? rawFocus
    : {
      enabled: value.presenterFocusEnabled === true || rawFocus === true,
    };
  return {
    ...value,
    studentAnnotationEnabled:
      value.studentAnnotationEnabled === true || value.studentAnnotationsEnabled === true,
    presenterFocus: {
      enabled: focus.enabled === true,
      presenterId: String(focus.presenterId || focus.presenter || ''),
      pageId: String(focus.pageId || ''),
      x: Number(focus.x) || 0,
      y: Number(focus.y) || 0,
      zoom: Number(focus.zoom) || 1,
      sourceId: String(focus.sourceId || ''),
      updatedAt: focus.updatedAt || null,
      expiresAt: focus.expiresAt || null,
    },
  };
};
const normalizeBoardPermissions = (value = {}) => ({
  ...value,
  role: normalizedRole(value.role),
  canView: value.canView === true,
  canEdit: value.canEdit === true,
  canAnnotate: value.canAnnotate === true,
  canManage: value.canManage === true || value.canModerate === true,
  canToggleStudentAnnotation: value.canToggleStudentAnnotation === true,
  canControlPresenterFocus: value.canControlPresenterFocus === true,
  canRelayPointer: value.canRelayPointer === true,
  isEligibleStudent: value.isEligibleStudent === true,
  isAdmitted: value.isAdmitted === true,
});
const hasCanonicalBoardAuthority = (value) => Boolean(
  value && typeof value === 'object' && [
    'canView',
    'canEdit',
    'canAnnotate',
    'canManage',
    'canToggleStudentAnnotation',
    'canControlPresenterFocus',
  ].some((key) => Object.prototype.hasOwnProperty.call(value, key))
);

const ClassBlackboard = ({
  roomId,
  socket,
  access = {},
  collaborationState = null,
  participantIdentity = null,
  onCollaborationStateChange,
  onEphemeralSignal,
  scriptureInsert = null,
  onScriptureInsertHandled,
  forceVisible = false,
  openRequestId = 0,
  minimizeRequestId = 0,
  onClose,
  participantDock = null,
}) => {
  const [liveCollaboration, setLiveCollaboration] = useState(() => normalizeCollaborationState({
    ...(access.blackboardCollaboration || {}),
    ...(collaborationState || {}),
  }));
  const [boardPermissions, setBoardPermissions] = useState(() => normalizeBoardPermissions(
    access.boardPermissions || access.permissions || {}
  ));
  const [authorityReady, setAuthorityReady] = useState(() => hasCanonicalBoardAuthority(
    access.boardPermissions || access.permissions
  ));
  const boardPermissionsRef = useRef(boardPermissions);
  boardPermissionsRef.current = boardPermissions;
  const applyBoardPermissions = useCallback((value) => {
    const normalized = normalizeBoardPermissions(value || {});
    boardPermissionsRef.current = normalized;
    setBoardPermissions(normalized);
    setAuthorityReady(true);
    return normalized;
  }, []);
  const suppliedAuthorityRef = useRef(null);
  suppliedAuthorityRef.current = {
    permissions: access.boardPermissions || access.permissions || {},
    collaboration: {
      ...(access.blackboardCollaboration || {}),
      ...(collaborationState || {}),
    },
  };
  const actorRole = normalizedRole(
    boardPermissions.role || participantIdentity?.role
  );
  const actorId = String(
    participantIdentity?.id || participantIdentity?._id || access.actorId || ''
  );
  const actorName = String(
    participantIdentity?.name || access.actorName || liveCollaboration.actorName || 'Participant'
  ).slice(0, 80);
  const isStudent = actorRole === 'student';
  const studentAnnotationEnabled = liveCollaboration.studentAnnotationEnabled === true;
  const canEdit = boardPermissions.canEdit === true || boardPermissions.canAnnotate === true;
  const canManageStudentAnnotations = boardPermissions.canToggleStudentAnnotation === true;
  const presenterFocusLocked = Boolean(
    liveCollaboration.presenterFocus?.enabled === true &&
    isStudent &&
    boardPermissions.isAdmitted === true
  );
  const canRepositionSharedBoard = canStudentRepositionSharedBoard(
    boardPermissions,
    actorRole
  );
  // Presenter focus still owns the shared page/zoom. Minimizing and moving the
  // preview only changes this student's layout and grants no board authority.
  const canMinimizeLocally = !presenterFocusLocked || canRepositionSharedBoard;
  const canMovePreview = canRepositionSharedBoard || (
    !presenterFocusLocked && (
      liveCollaboration.canMovePreview === true || boardPermissions.canEdit === true
    )
  );
  const canControlSharedBoard = boardPermissions.canManage === true;
  const draftUserId = actorId || getUserId?.() || '';
  const draftWarningRef = useRef(false);
  const flushBoardRef = useRef(null);
  const sourceIdRef = useRef(makeId());
  const boardRoomIdRef = useRef(String(roomId || ''));
  const clientRevisionRef = useRef(0);
  const remoteRevisionRef = useRef(new Map());
  const pagesRef = useRef([emptyPage()]);
  const activePageIdRef = useRef('');
  const visibleRef = useRef(false);
  const saveTimerRef = useRef(null);
  const persistQueueRef = useRef(Promise.resolve());
  const persistLatestRef = useRef(null);
  const recoverLatestRef = useRef(null);
  const lastPersistedRevisionRef = useRef(0);
  const joinedSocketIdRef = useRef('');
  const pendingLiveSnapshotRef = useRef(null);
  const pendingOpenStateRef = useRef(null);
  const svgRef = useRef(null);
  const imageInputRef = useRef(null);
  const textInputRef = useRef(null);
  const inlineTextInputRef = useRef(null);
  const inlineEditorRef = useRef(null);
  const finishInlineEditorRef = useRef(() => undefined);
  const operationRef = useRef(null);
  const pointerEndRef = useRef(null);
  const pendingStrokeSamplesRef = useRef([]);
  const strokeFrameRef = useRef(null);
  const lastPenInputAtRef = useRef(0);
  const remoteFrameRef = useRef(null);
  const pendingRemoteRef = useRef(null);
  const confirmedPagesRef = useRef(pagesRef.current);
  const confirmedPageIdRef = useRef('');
  const activeTextTargetRef = useRef('sidebar');
  const ephemeralFrameRef = useRef(null);
  const pendingEphemeralRef = useRef(null);
  const pointerSequenceRef = useRef(0);
  const previewRef = useRef(null);
  const previewDragRef = useRef(null);
  const handledScriptureIdsRef = useRef(new Set());
  const lastBoardPointRef = useRef({ x: BOARD_WIDTH / 2, y: BOARD_HEIGHT / 2 });

  const [visible, setVisibleState] = useState(false);
  const [pages, setPagesState] = useState(pagesRef.current);
  const [activePageId, setActivePageIdState] = useState('');
  const [tool, setTool] = useState('pen');
  const [stroke, setStroke] = useState('#111827');
  const [fill, setFill] = useState('#fef08a');
  const [shapeFilled, setShapeFilled] = useState(false);
  const [strokeWidth, setStrokeWidth] = useState(5);
  const [lineStyle, setLineStyle] = useState('solid');
  const [textValue, setTextValue] = useState('');
  const [fontSize, setFontSize] = useState(44);
  const [fontFamily, setFontFamily] = useState(ORIGINAL_LANGUAGE_FONT);
  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState('⭐');
  const [selectedId, setSelectedId] = useState('');
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const [saveState, setSaveState] = useState('saved');
  const [recoveryDraft, setRecoveryDraft] = useState(null);
  const [handwriting, setHandwriting] = useState(null);
  const [recognitionBusy, setRecognitionBusy] = useState(false);
  const [recognitionError, setRecognitionError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);
  const [viewSize, setViewSize] = useState('large');
  const [zoom, setZoom] = useState(1);
  const [minimized, setMinimized] = useState(false);
  const [inlineEditor, setInlineEditor] = useState(null);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [keyboardLanguage, setKeyboardLanguage] = useState('greek');
  const [ephemeralPointers, setEphemeralPointers] = useState({});
  const [previewPosition, setPreviewPosition] = useState(null);

  const currentPage = useMemo(() => pages.find((page) => page.pageId === activePageId) || pages[0], [pages, activePageId]);
  const selectedElement = useMemo(() => currentPage?.elements?.find((item) => item.id === selectedId), [currentPage, selectedId]);
  const dash = useMemo(() => lineStyle === 'dashed' ? [18, 12] : lineStyle === 'dotted' ? [3, 12] : [], [lineStyle]);

  const setVisible = useCallback((next) => { visibleRef.current = next; setVisibleState(next); }, []);
  const setPages = useCallback((next) => { pagesRef.current = next; setPagesState(next); }, []);
  const setActivePageId = useCallback((next) => { activePageIdRef.current = next; setActivePageIdState(next); }, []);

  useEffect(() => {
    setLiveCollaboration((current) => normalizeCollaborationState({
      ...current,
      ...(access.blackboardCollaboration || {}),
      ...(collaborationState || {}),
    }));
  }, [access.blackboardCollaboration, collaborationState]);

  useEffect(() => {
    const supplied = access.boardPermissions || access.permissions;
    if (hasCanonicalBoardAuthority(supplied)) applyBoardPermissions(supplied);
  }, [access.boardPermissions, access.permissions, applyBoardPermissions]);

  useEffect(() => {
    if (canMinimizeLocally || !minimized) return;
    setMinimized(false);
  }, [canMinimizeLocally, minimized]);

  useEffect(() => {
    if (!presenterFocusLocked) return;
    const focus = liveCollaboration.presenterFocus;
    if (focus.pageId && pagesRef.current.some((page) => page.pageId === focus.pageId)) {
      setActivePageId(focus.pageId);
    }
    setZoom(clamp(Number(focus.zoom) || 1, 0.35, 2.5));
  }, [
    liveCollaboration.presenterFocus?.pageId,
    liveCollaboration.presenterFocus?.zoom,
    presenterFocusLocked,
    setActivePageId,
  ]);

  const applyInitialBoard = useCallback((board, { syncVisibility = false } = {}) => {
    if (!board) return;
    const nextPages = safePages(board.pages);
    setPages(nextPages);
    setActivePageId(board.activePageId || nextPages[0].pageId);
    confirmedPagesRef.current = deepClone(nextPages);
    confirmedPageIdRef.current = board.activePageId || nextPages[0].pageId;
    if (board.collaborationState || board.collaboration) {
      setLiveCollaboration((current) => normalizeCollaborationState({
        ...current,
        ...(board.collaborationState || board.collaboration),
      }));
    }
    if (board.isOpen === true) setVisible(true);
    else if (syncVisibility) {
      inlineEditorRef.current = null;
      setInlineEditor(null);
      setMinimized(false);
      setVisible(false);
    }
  }, [setActivePageId, setPages, setVisible]);

  useEffect(() => {
    if (!roomId) return undefined;
    let cancelled = false;
    let initialLoadComplete = false;
    let joinInFlight = false;
    let joinAttempt = 0;
    let retryTimer = null;
    let acknowledgementTimer = null;

    if (boardRoomIdRef.current !== String(roomId)) {
      boardRoomIdRef.current = String(roomId);
      pendingLiveSnapshotRef.current = null;
      pendingOpenStateRef.current = null;
      clientRevisionRef.current = 0;
      lastPersistedRevisionRef.current = 0;
      confirmedPagesRef.current = [emptyPage()];
      confirmedPageIdRef.current = '';
      inlineEditorRef.current = null;
      setInlineEditor(null);
      const resetPage = emptyPage();
      setPages([resetPage]);
      setActivePageId(resetPage.pageId);
      setVisible(false);
      setMinimized(false);
      const suppliedPermissions = suppliedAuthorityRef.current.permissions;
      const resetPermissions = normalizeBoardPermissions(suppliedPermissions);
      boardPermissionsRef.current = resetPermissions;
      setBoardPermissions(resetPermissions);
      setAuthorityReady(hasCanonicalBoardAuthority(suppliedPermissions));
      setLiveCollaboration(normalizeCollaborationState(
        suppliedAuthorityRef.current.collaboration
      ));
    }
    joinedSocketIdRef.current = '';
    remoteRevisionRef.current.clear();

    const scheduleJoin = (delay = 650) => {
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(joinBoard, delay);
    };

    const joinBoard = () => {
      if (cancelled || !initialLoadComplete) return;
      if (!socket?.connected) {
        scheduleJoin(650);
        return;
      }

      const socketId = String(socket.id || 'connected');
      if (joinInFlight || joinedSocketIdRef.current === socketId) return;

      joinInFlight = true;
      const attempt = ++joinAttempt;
      window.clearTimeout(acknowledgementTimer);
      acknowledgementTimer = window.setTimeout(() => {
        if (cancelled || attempt !== joinAttempt) return;
        joinInFlight = false;
        scheduleJoin(900);
      }, 5000);

      socket.emit('board:join', { roomId }, (result = {}) => {
        if (cancelled || attempt !== joinAttempt) return;
        window.clearTimeout(acknowledgementTimer);
        joinInFlight = false;

        if (!result.success) {
          scheduleJoin(900);
          return;
        }

        joinedSocketIdRef.current = socketId;
        const pendingSnapshot = pendingLiveSnapshotRef.current;
        const joinedPermissions = result.permissions
          ? normalizeBoardPermissions(result.permissions)
          : boardPermissionsRef.current;
        const joinedCanEdit = joinedPermissions.canEdit === true || joinedPermissions.canAnnotate === true;
        const joinedCanManage = joinedPermissions.canManage === true;
        // A local edit made during reconnection is newer than the join
        // snapshot. Keep it and flush exactly one latest live snapshot.
        if (result.board && (!pendingSnapshot || !joinedCanEdit)) {
          applyInitialBoard(result.board, {
            syncVisibility: pendingOpenStateRef.current === null,
          });
        }
        if (result.collaboration || result.collaborationState) {
          setLiveCollaboration((current) => normalizeCollaborationState({
            ...current,
            ...(result.collaboration || result.collaborationState),
          }));
        }
        if (result.permissions) applyBoardPermissions(joinedPermissions);

        // Socket.IO preserves event order. Re-open first so Core does not
        // correctly reject an offline edit as a snapshot for a closed board.
        if (pendingOpenStateRef.current !== null && joinedCanManage) {
          socket.emit('board:open', {
            roomId,
            open: pendingOpenStateRef.current,
            sourceId: sourceIdRef.current,
          });
          pendingOpenStateRef.current = null;
        }

        if (pendingSnapshot && joinedCanEdit) {
          socket.emit('board:live-snapshot', pendingSnapshot, (snapshotResult = {}) => {
            if (snapshotResult.success === false) {
              toast.error(snapshotResult.error || 'Offline blackboard update was denied', {
                toastId: `blackboard-reconnect-${snapshotResult.code || 'denied'}`,
              });
              void recoverLatestRef.current?.();
              return;
            }
            void persistLatestRef.current?.(
              pendingSnapshot.pages,
              pendingSnapshot.activePageId,
              pendingSnapshot.clientRevision
            ).catch(() => undefined);
          });
        } else if (pendingSnapshot) {
          pendingLiveSnapshotRef.current = null;
          setSaveState('error');
          toast.error('Your offline blackboard changes were not applied because annotation access changed.', {
            toastId: 'blackboard-reconnect-permission-changed',
          });
        }
      });
    };

    const handleConnect = () => {
      joinedSocketIdRef.current = '';
      joinInFlight = false;
      joinAttempt += 1;
      window.clearTimeout(acknowledgementTimer);
      joinBoard();
    };
    const handleDisconnect = () => {
      joinedSocketIdRef.current = '';
      joinInFlight = false;
      joinAttempt += 1;
      window.clearTimeout(acknowledgementTimer);
      window.clearTimeout(retryTimer);
    };

    socket?.on('connect', handleConnect);
    socket?.on('disconnect', handleDisconnect);

    const start = async () => {
      try {
        const { data } = await api.get(`/class-boards/${encodeURIComponent(roomId)}/current`);
        if (!cancelled) {
          applyInitialBoard(data?.board);
          const draft = readClassBoardDraft(roomId, draftUserId);
          if (draft?.pages?.length) {
            if (draft.sourceId === data?.board?.lastSourceId && Number(draft.clientRevision) <= Number(data?.board?.lastClientRevision)) {
              clearClassBoardDraft(roomId, draftUserId, draft.sourceId, draft.clientRevision);
            } else setRecoveryDraft(draft);
          }
          if (data?.collaboration) {
            setLiveCollaboration((current) => normalizeCollaborationState({
              ...current,
              ...data.collaboration,
            }));
          }
          if (data?.permissions) applyBoardPermissions(data.permissions);
        }
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.error || 'Unable to load blackboard');
      } finally {
        if (!cancelled) setAuthorityReady(true);
        initialLoadComplete = true;
        joinBoard();
      }
    };
    void start();
    return () => {
      cancelled = true;
      joinAttempt += 1;
      socket?.off('connect', handleConnect);
      socket?.off('disconnect', handleDisconnect);
      window.clearTimeout(retryTimer);
      window.clearTimeout(acknowledgementTimer);
      window.clearTimeout(saveTimerRef.current);
      joinedSocketIdRef.current = '';
    };
  }, [
    applyInitialBoard,
    applyBoardPermissions,
    draftUserId,
    roomId,
    setActivePageId,
    setPages,
    setVisible,
    socket,
  ]);

  useEffect(() => {
    if (!forceVisible) return;
    setVisible(true);
    setMinimized(false);
    pendingOpenStateRef.current = canControlSharedBoard ? true : null;
    if (
      canControlSharedBoard &&
      socket?.connected &&
      joinedSocketIdRef.current === String(socket.id || 'connected')
    ) {
      socket.emit('board:open', {
        roomId,
        open: true,
        sourceId: sourceIdRef.current,
      }, async (result = {}) => {
        if (result.success === false) {
          toast.error(result.error || 'Unable to open blackboard');
          return;
        }
        try {
          const { data } = await api.get(`/class-boards/${encodeURIComponent(roomId)}/current`);
          if (data?.permissions) applyBoardPermissions(data.permissions);
          if (data?.collaboration) {
            setLiveCollaboration((current) => normalizeCollaborationState({
              ...current,
              ...data.collaboration,
            }));
          }
        } catch (error) {
          console.warn('Unable to refresh blackboard after opening:', error);
        }
      });
      pendingOpenStateRef.current = null;
    }
  }, [applyBoardPermissions, canControlSharedBoard, forceVisible, openRequestId, roomId, setVisible, socket]);

  useEffect(() => {
    if (!socket || !roomId) return undefined;
    const opened = (event = {}) => {
      if (String(event.roomId || '') === String(roomId)) {
        pendingOpenStateRef.current = null;
        if (event.collaboration) {
          setLiveCollaboration((current) => normalizeCollaborationState({
            ...current,
            ...event.collaboration,
          }));
        }
        setVisible(true);
        void api.get(`/class-boards/${encodeURIComponent(roomId)}/current`)
          .then(({ data }) => {
            if (data?.permissions) applyBoardPermissions(data.permissions);
            if (data?.collaboration) {
              setLiveCollaboration((current) => normalizeCollaborationState({
                ...current,
                ...data.collaboration,
              }));
            }
          })
          .catch(() => undefined);
      }
    };
    const closed = (event = {}) => {
      if (String(event.roomId || '') === String(roomId)) {
        pendingOpenStateRef.current = null;
        if (event.collaboration) {
          setLiveCollaboration(normalizeCollaborationState(event.collaboration));
        }
        inlineEditorRef.current = null;
        setInlineEditor(null);
        setMinimized(false);
        setVisible(false);
        onClose?.();
      }
    };
    const receive = ({ roomId: eventRoomId, pages: incoming, activePageId: incomingPageId, sourceId = '', clientRevision = 0 } = {}) => {
      if (String(eventRoomId || '') !== String(roomId) || !Array.isArray(incoming) || sourceId === sourceIdRef.current) return;
      const previous = remoteRevisionRef.current.get(sourceId) || 0;
      if (sourceId && Number(clientRevision) <= previous) return;
      if (sourceId) remoteRevisionRef.current.set(sourceId, Number(clientRevision) || previous + 1);
      pendingRemoteRef.current = {
        pages: incoming,
        activePageId: incomingPageId || incoming[0]?.pageId || '',
      };
      if (!remoteFrameRef.current) {
        remoteFrameRef.current = window.requestAnimationFrame(() => {
          remoteFrameRef.current = null;
          const pending = pendingRemoteRef.current;
          pendingRemoteRef.current = null;
          if (!pending) return;
          // Apply only the newest snapshot received in this paint cycle. This
          // keeps the SVG mounted and removes the visible remote-board blink.
          setPages(pending.pages);
          setActivePageId(pending.activePageId);
          confirmedPagesRef.current = deepClone(pending.pages);
          confirmedPageIdRef.current = pending.activePageId;
          setSelectedId('');
        });
      }
    };
    const saved = ({ board, sourceId, clientRevision, roomId: eventRoomId } = {}) => {
      if (!board?.pages) return;
      receive({
        roomId: eventRoomId || board.roomId,
        pages: board.pages,
        activePageId: board.activePageId,
        sourceId,
        clientRevision,
      });
    };
    socket.on('board:opened', opened);
    socket.on('board:closed', closed);
    socket.on('board:live-snapshot', receive);
    socket.on('board:snapshot', saved);
    return () => {
      socket.off('board:opened', opened); socket.off('board:closed', closed);
      socket.off('board:live-snapshot', receive); socket.off('board:snapshot', saved);
      if (remoteFrameRef.current) window.cancelAnimationFrame(remoteFrameRef.current);
      remoteFrameRef.current = null;
      pendingRemoteRef.current = null;
    };
  }, [applyBoardPermissions, onClose, roomId, setActivePageId, setPages, setVisible, socket]);

  const hydrateAuthority = useCallback((data = {}) => {
    if (data.collaboration || data.board?.collaboration) {
      setLiveCollaboration((current) => normalizeCollaborationState({
        ...current,
        ...(data.board?.collaboration || {}),
        ...(data.collaboration || {}),
      }));
    }
    if (data.permissions) {
      applyBoardPermissions(data.permissions);
    }
  }, [applyBoardPermissions]);

  const refreshBoardAuthority = useCallback(async () => {
    try {
      const { data } = await api.get(`/class-boards/${encodeURIComponent(roomId)}/current`);
      hydrateAuthority(data);
      return data;
    } catch (error) {
      console.warn('Unable to refresh blackboard permissions:', error);
      return null;
    }
  }, [hydrateAuthority, roomId]);

  useEffect(() => {
    if (!socket || !roomId) return undefined;
    const receiveCollaborationState = (event = {}) => {
      if (String(event.roomId || '') !== String(roomId)) return;
      const nextState = event.collaboration || event.collaborationState || event.state || event;
      setLiveCollaboration((current) => normalizeCollaborationState({ ...current, ...nextState }));
      void refreshBoardAuthority();
    };
    const receivePresenterFocus = (event = {}) => {
      if (String(event.roomId || '') !== String(roomId)) return;
      const focus = normalizeCollaborationState({ presenterFocus: event.presenterFocus || event }).presenterFocus;
      setLiveCollaboration((current) => normalizeCollaborationState({
        ...current,
        presenterFocus: focus,
      }));
      if (focus.enabled && isStudent) {
        if (focus.pageId && pagesRef.current.some((page) => page.pageId === focus.pageId)) {
          setActivePageId(focus.pageId);
        }
        setZoom(clamp(Number(focus.zoom) || 1, 0.35, 2.5));
      }
    };
    const receivePointer = (kind) => (event = {}) => {
      if (
        String(event.roomId || '') !== String(roomId) ||
        event.sourceId === sourceIdRef.current
      ) return;
      const identity = String(event.socketId || event.userId || event.sourceId || 'participant');
      const key = `${identity}:${kind}`;
      const serverExpiry = new Date(event.expiresAt || 0).getTime();
      setEphemeralPointers((current) => ({
        ...current,
        [key]: {
          ...event,
          kind,
          name: event.displayName || event.name || 'Participant',
          colour: event.colour || participantColour(identity),
          expiresAt: Number.isFinite(serverExpiry) && serverExpiry > Date.now()
            ? serverExpiry
            : Date.now() + (kind === 'laser' ? 1500 : 5000),
        },
      }));
    };
    const receiveLegacyEphemeral = (event = {}) => receivePointer(
      event.kind === 'laser' ? 'laser' : 'cursor'
    )(event);
    const clearPointers = (event = {}) => {
      if (String(event.roomId || '') !== String(roomId)) return;
      const kinds = new Set(event.kinds || ['cursor', 'laser']);
      setEphemeralPointers((current) => Object.fromEntries(
        Object.entries(current).filter(([, pointer]) => {
          if (event.all === true) return false;
          const sameSocket = event.socketId && String(pointer.socketId || '') === String(event.socketId);
          const sameUser = event.userId && String(pointer.userId || '') === String(event.userId);
          return !(kinds.has(pointer.kind) && (sameSocket || sameUser));
        })
      ));
    };
    const receiveCursor = receivePointer('cursor');
    const receiveLaser = receivePointer('laser');
    socket.on('board:collaboration', receiveCollaborationState);
    socket.on('board:presenter-focus', receivePresenterFocus);
    socket.on('board:cursor', receiveCursor);
    socket.on('board:laser', receiveLaser);
    socket.on('board:pointer-cleared', clearPointers);
    // Compatibility listeners may be removed after every deployed Core uses
    // the canonical events above.
    socket.on('board:collaboration-state', receiveCollaborationState);
    socket.on('board:ephemeral', receiveLegacyEphemeral);
    const expiryTimer = window.setInterval(() => {
      const now = Date.now();
      setEphemeralPointers((current) => {
        const entries = Object.entries(current).filter(([, pointer]) => pointer.expiresAt > now);
        return entries.length === Object.keys(current).length
          ? current
          : Object.fromEntries(entries);
      });
    }, 500);
    return () => {
      socket.off('board:collaboration', receiveCollaborationState);
      socket.off('board:presenter-focus', receivePresenterFocus);
      socket.off('board:cursor', receiveCursor);
      socket.off('board:laser', receiveLaser);
      socket.off('board:pointer-cleared', clearPointers);
      socket.off('board:collaboration-state', receiveCollaborationState);
      socket.off('board:ephemeral', receiveLegacyEphemeral);
      window.clearInterval(expiryTimer);
      if (ephemeralFrameRef.current) window.cancelAnimationFrame(ephemeralFrameRef.current);
      ephemeralFrameRef.current = null;
      pendingEphemeralRef.current = null;
    };
  }, [isStudent, refreshBoardAuthority, roomId, setActivePageId, socket]);

  const publishEphemeral = useCallback((kind, at, active = true) => {
    if (!at || !visibleRef.current || boardPermissions.canRelayPointer !== true) return;
    const normalizedKind = kind === 'laser' ? 'laser' : 'cursor';
    pendingEphemeralRef.current = {
      roomId,
      sourceId: sourceIdRef.current,
      pageId: activePageIdRef.current,
      kind: normalizedKind,
      x: clamp(Number(at.x), 0, BOARD_WIDTH),
      y: clamp(Number(at.y), 0, BOARD_HEIGHT),
      colour: participantColour(actorId || sourceIdRef.current),
      clientSequence: ++pointerSequenceRef.current,
      active,
    };
    if (ephemeralFrameRef.current) return;
    ephemeralFrameRef.current = window.requestAnimationFrame(() => {
      ephemeralFrameRef.current = null;
      const pending = pendingEphemeralRef.current;
      pendingEphemeralRef.current = null;
      if (!pending) return;
      const localKey = `${sourceIdRef.current}:${pending.kind}`;
      setEphemeralPointers((current) => {
        if (pending.active === false) {
          const next = { ...current };
          delete next[localKey];
          return next;
        }
        return {
          ...current,
          [localKey]: {
            ...pending,
            name: actorName,
            expiresAt: Date.now() + (pending.kind === 'laser' ? 1500 : 5000),
          },
        };
      });
      if (pending.active === false) return;
      const eventName = `board:${pending.kind}`;
      const payload = {
        roomId: pending.roomId,
        pageId: pending.pageId,
        x: pending.x,
        y: pending.y,
        colour: pending.colour,
        sourceId: pending.sourceId,
        clientSequence: pending.clientSequence,
      };
      if (typeof onEphemeralSignal === 'function') {
        onEphemeralSignal(payload, eventName);
      } else if (socket?.connected) {
        socket.emit(eventName, payload, (result = {}) => {
          if (result.success === false) {
            setEphemeralPointers((current) => {
              const next = { ...current };
              delete next[localKey];
              return next;
            });
            toast.error(result.error || 'Blackboard pointer sharing was denied', {
              toastId: `board-pointer-${result.code || 'denied'}`,
            });
          }
        });
      }
    });
  }, [actorId, actorName, boardPermissions.canRelayPointer, onEphemeralSignal, roomId, socket]);

  const requestStudentAnnotationChange = useCallback(async (enabled) => {
    if (!canManageStudentAnnotations) return;
    const previous = liveCollaboration;
    const optimistic = normalizeCollaborationState({
      ...liveCollaboration,
      studentAnnotationEnabled: Boolean(enabled),
    });
    setLiveCollaboration(optimistic);
    onCollaborationStateChange?.(optimistic);
    try {
      const { data } = await api.patch(
        `/class-boards/${encodeURIComponent(roomId)}/collaboration`,
        { studentAnnotationEnabled: Boolean(enabled) }
      );
      hydrateAuthority(data);
    } catch (error) {
      setLiveCollaboration(previous);
      toast.error(error.response?.data?.error || 'Unable to update student annotation access');
    }
  }, [
    canManageStudentAnnotations,
    hydrateAuthority,
    liveCollaboration,
    onCollaborationStateChange,
    roomId,
  ]);

  const requestPresenterFocusChange = useCallback(async (enabled) => {
    if (boardPermissions.canControlPresenterFocus !== true) return;
    const previous = liveCollaboration;
    const focus = {
      enabled: Boolean(enabled),
      presenterId: actorId,
      pageId: activePageIdRef.current,
      x: BOARD_WIDTH / 2,
      y: BOARD_HEIGHT / 2,
      zoom,
      sourceId: sourceIdRef.current,
    };
    setLiveCollaboration((current) => normalizeCollaborationState({
      ...current,
      presenterFocus: focus,
    }));
    try {
      const { data } = await api.patch(
        `/class-boards/${encodeURIComponent(roomId)}/collaboration`,
        {
          presenterFocusEnabled: Boolean(enabled),
          ...(enabled ? { presenterFocus: focus } : {}),
        }
      );
      hydrateAuthority(data);
    } catch (error) {
      setLiveCollaboration(previous);
      toast.error(error.response?.data?.error || 'Unable to update presenter focus');
    }
  }, [
    actorId,
    boardPermissions.canControlPresenterFocus,
    hydrateAuthority,
    liveCollaboration,
    roomId,
    zoom,
  ]);

  useEffect(() => {
    if (
      !visible ||
      liveCollaboration.presenterFocus?.enabled !== true ||
      boardPermissions.canControlPresenterFocus !== true ||
      !socket?.connected
    ) return undefined;
    const publishFocus = () => {
      socket.emit('board:presenter-focus', {
        roomId,
        enabled: true,
        pageId: activePageIdRef.current,
        x: BOARD_WIDTH / 2,
        y: BOARD_HEIGHT / 2,
        zoom,
        sourceId: sourceIdRef.current,
      }, (result = {}) => {
        if (result.success === false) {
          toast.error(result.error || 'Presenter focus update was denied', {
            toastId: `board-focus-${result.code || 'denied'}`,
          });
        } else if (result.presenterFocus) {
          setLiveCollaboration((current) => normalizeCollaborationState({
            ...current,
            presenterFocus: result.presenterFocus,
          }));
        }
      });
    };
    publishFocus();
    const timer = window.setInterval(publishFocus, 20_000);
    return () => window.clearInterval(timer);
  }, [
    activePageId,
    boardPermissions.canControlPresenterFocus,
    liveCollaboration.presenterFocus?.enabled,
    roomId,
    socket,
    visible,
    zoom,
  ]);

  const recoverAuthoritativeBoard = useCallback(async () => {
    window.clearTimeout(saveTimerRef.current);
    pendingLiveSnapshotRef.current = null;
    setPages(deepClone(confirmedPagesRef.current));
    setActivePageId(
      confirmedPageIdRef.current || confirmedPagesRef.current[0]?.pageId || ''
    );
    setSelectedId('');
    try {
      const { data } = await api.get(`/class-boards/${encodeURIComponent(roomId)}/current`);
      if (data?.board) applyInitialBoard(data.board, { syncVisibility: true });
      hydrateAuthority(data);
    } catch (error) {
      console.warn('Unable to reload authoritative blackboard state:', error);
    }
  }, [applyInitialBoard, hydrateAuthority, roomId, setActivePageId, setPages]);
  recoverLatestRef.current = recoverAuthoritativeBoard;

  const persist = useCallback((nextPages, nextPageId, revision, options = {}) => {
    setSaveState('saving');
    const task = persistQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        const { data } = await api.put(`/class-boards/${encodeURIComponent(roomId)}/current`, {
          pages: nextPages,
          activePageId: nextPageId,
          isOpen: visibleRef.current,
          sourceId: sourceIdRef.current,
          clientRevision: revision,
          finalFlush: options.finalFlush === true,
        });
        clearClassBoardDraft(roomId, draftUserId, sourceIdRef.current, revision);
        hydrateAuthority(data);
        lastPersistedRevisionRef.current = Math.max(
          lastPersistedRevisionRef.current,
          Number(revision) || 0
        );
        if (Number(revision) >= clientRevisionRef.current) {
          const savedPages = safePages(data?.board?.pages || nextPages);
          const savedPageId = data?.board?.activePageId || nextPageId || savedPages[0].pageId;
          confirmedPagesRef.current = deepClone(savedPages);
          confirmedPageIdRef.current = savedPageId;
          if (
            Number(pendingLiveSnapshotRef.current?.clientRevision || 0) <= Number(revision)
          ) {
            pendingLiveSnapshotRef.current = null;
          }
          // A new stroke does not get a revision until pointer-up. An older
          // autosave can therefore have the same revision while the next word
          // is being written. Never replace that in-progress local drawing.
          if (!operationRef.current) {
            setPages(savedPages);
            setActivePageId(savedPageId);
            setSaveState('saved');
          } else setSaveState('dirty');
        }
      })
      .catch((error) => {
        if (Number(revision) >= clientRevisionRef.current) {
          const status = Number(error.response?.status || 0);
          const authoritativeRejection = status >= 400 && status < 500;
          setSaveState(authoritativeRejection ? 'error' : 'dirty');
          toast.error(
            authoritativeRejection
              ? error.response?.data?.error || 'Blackboard save was denied'
              : 'Blackboard save is waiting for the connection to recover.',
            { toastId: `blackboard-save-${roomId}-${revision}` }
          );
          if (authoritativeRejection) void recoverAuthoritativeBoard();
        }
        throw error;
      });
    // Keep the serialization chain alive after an individual failed request;
    // callers still receive the real task and can await/catch it.
    persistQueueRef.current = task.catch(() => undefined);
    return task;
  }, [draftUserId, hydrateAuthority, recoverAuthoritativeBoard, roomId, setActivePageId, setPages]);
  persistLatestRef.current = persist;

  const syncBoard = useCallback((nextPages = pagesRef.current, nextPageId = activePageIdRef.current, immediate = false) => {
    const currentPermissions = boardPermissionsRef.current;
    if (currentPermissions.canEdit !== true && currentPermissions.canAnnotate !== true) return;
    const revision = ++clientRevisionRef.current;
    const payload = {
      roomId, pages: nextPages, activePageId: nextPageId,
      sourceId: sourceIdRef.current, clientRevision: revision,
    };
    // Retain the newest unsaved payload even when the socket currently works.
    // It is cleared only after durable REST persistence succeeds.
    pendingLiveSnapshotRef.current = payload;
    if (!writeClassBoardDraft(roomId, draftUserId, payload) && !draftWarningRef.current) {
      draftWarningRef.current = true;
      toast.info('Local recovery storage is unavailable. Wait for Saved before closing the browser.');
    }
    if (
      socket?.connected &&
      joinedSocketIdRef.current === String(socket.id || 'connected')
    ) {
      socket.emit('board:live-snapshot', payload, (result = {}) => {
        if (result.success === false && revision >= clientRevisionRef.current) {
          setSaveState('error');
          toast.error(result.error || 'Blackboard live update was denied', {
            toastId: `blackboard-live-${result.code || 'denied'}`,
          });
          void recoverAuthoritativeBoard();
        }
      });
    } else {
      // Socket.IO buffers every emit while offline. Retaining only the latest
      // board snapshot prevents a reconnect burst and stale duplicate writes.
      pendingLiveSnapshotRef.current = payload;
    }
    setSaveState('dirty');
    window.clearTimeout(saveTimerRef.current);
    if (immediate) void persist(nextPages, nextPageId, revision).catch(() => undefined);
    else saveTimerRef.current = window.setTimeout(
      () => void persist(
        pagesRef.current,
        activePageIdRef.current,
        clientRevisionRef.current
      ).catch(() => undefined),
      650
    );
  }, [draftUserId, persist, recoverAuthoritativeBoard, roomId, socket]);

  const pushHistory = useCallback((elements) => {
    setUndoStack((items) => [...items.slice(-(MAX_HISTORY - 1)), deepClone(elements)]);
    setRedoStack([]);
  }, []);

  const updatePageElements = useCallback((pageId, elements, { history = true, sync = true } = {}) => {
    const page = pagesRef.current.find((item) => item.pageId === pageId);
    if (!page) return;
    if (history) pushHistory(page.elements);
    const next = pagesRef.current.map((item) => item.pageId === page.pageId ? { ...item, elements } : item);
    setPages(next);
    if (sync) syncBoard(next, activePageIdRef.current || page.pageId);
  }, [pushHistory, setPages, syncBoard]);

  const updateActiveElements = useCallback((elements, options = {}) => {
    updatePageElements(activePageIdRef.current, elements, options);
  }, [updatePageElements]);

  const updateShape = (element, start, at) => {
    if (['line', 'arrow', 'triangle'].includes(element.type)) return { ...element, x1: start.x, y1: start.y, x2: at.x, y2: at.y };
    if (element.type === 'rect') return { ...element, x: Math.min(start.x, at.x), y: Math.min(start.y, at.y), width: Math.max(2, Math.abs(at.x - start.x)), height: Math.max(2, Math.abs(at.y - start.y)) };
    if (element.type === 'ellipse') return { ...element, cx: (start.x + at.x) / 2, cy: (start.y + at.y) / 2, rx: Math.max(2, Math.abs(at.x - start.x) / 2), ry: Math.max(2, Math.abs(at.y - start.y) / 2) };
    return element;
  };

  const eraseAt = useCallback((at) => {
    const page = pagesRef.current.find((item) => item.pageId === activePageIdRef.current) || pagesRef.current[0];
    const radius = Math.max(18, strokeWidth * 2.5);
    const elements = page.elements.filter((element) => {
      if (element.locked) return true;
      const bounds = boundsOf(element);
      return !bounds || at.x < bounds.x - radius || at.x > bounds.x + bounds.width + radius || at.y < bounds.y - radius || at.y > bounds.y + bounds.height + radius;
    });
    if (elements.length !== page.elements.length) {
      const next = pagesRef.current.map((item) => item.pageId === page.pageId ? { ...item, elements } : item);
      setPages(next);
    }
  }, [setPages, strokeWidth]);

  const flushPendingStrokeSamples = useCallback(() => {
    if (strokeFrameRef.current) {
      window.cancelAnimationFrame(strokeFrameRef.current);
      strokeFrameRef.current = null;
    }
    const samples = pendingStrokeSamplesRef.current;
    pendingStrokeSamplesRef.current = [];
    const operation = operationRef.current;
    const svg = svgRef.current;
    if (!samples.length || operation?.mode !== 'path' || !svg) return;

    const pageId = activePageIdRef.current;
    const minimumDistance = getBoardSampleSpacing(
      svg,
      BOARD_WIDTH,
      globalThis.devicePixelRatio || 1
    );
    const next = pagesRef.current.map((page) => {
      if (page.pageId !== pageId) return page;
      return {
        ...page,
        elements: page.elements.map((element) => {
          if (element.id !== operation.elementId) return element;
          return {
            ...element,
            points: appendStrokeSamples(
              element.points,
              samples,
              (sample) => pointerPoint(sample, svg),
              minimumDistance
            ),
          };
        }),
      };
    });
    // pagesRef is updated synchronously by setPages, so pointer-up can flush
    // the last stylus samples and persist one complete stroke without racing a
    // queued React render.
    setPages(next);
  }, [setPages]);

  const queueStrokeSamples = useCallback((samples) => {
    if (!samples?.length) return;
    pendingStrokeSamplesRef.current.push(...snapshotPointerSamples(samples));
    if (strokeFrameRef.current) return;
    strokeFrameRef.current = window.requestAnimationFrame(() => {
      strokeFrameRef.current = null;
      flushPendingStrokeSamples();
    });
  }, [flushPendingStrokeSamples]);

  useEffect(() => () => {
    if (strokeFrameRef.current) {
      window.cancelAnimationFrame(strokeFrameRef.current);
      strokeFrameRef.current = null;
    }
    pendingStrokeSamplesRef.current = [];
  }, []);

  const createTextElement = (elementType, at, rawValue = textValue, style = {}) => {
    const value = normalizeElementText(rawValue, elementType);
    if (!value.trim()) {
      toast.info('Type or paste text in the sidebar first');
      textInputRef.current?.focus();
      return null;
    }

    const equation = elementType === 'equation';
    const direction = getTextDirection(value);
    const language = getTextLanguage(value);
    const textStyle = {
      text: value,
      colour: style.colour || stroke,
      fontSize: Number(style.fontSize) || fontSize,
      fontFamily: equation
        ? '"Cambria Math", "STIX Two Math", "DejaVu Serif", serif'
        : style.fontFamily || fontFamily,
      fontWeight: style.fontWeight || (bold ? 'bold' : 'normal'),
      fontStyle: style.fontStyle || (italic ? 'italic' : 'normal'),
      textDecoration: style.textDecoration || (underline ? 'underline' : 'none'),
      direction,
      language,
      opacity: 1,
      locked: false,
    };

    if (elementType === 'sticky') {
      return {
        id: makeId(),
        type: 'sticky',
        x: clamp(at.x, 0, BOARD_WIDTH - 340),
        y: clamp(at.y, 0, BOARD_HEIGHT - 210),
        width: Number(style.width) || 340,
        height: Number(style.height) || 210,
        fill: style.fill || fill,
        ...textStyle,
        fontSize: Math.min(Number(textStyle.fontSize) || fontSize, 54),
      };
    }

    return {
      id: makeId(),
      type: 'text',
      ...(equation ? { semanticType: 'equation' } : {}),
      x: at.x,
      y: at.y,
      width: clamp(Number(style.width) || (equation ? 560 : 720), 180, 1400),
      height: clamp(Number(style.height) || 220, 120, 820),
      ...textStyle,
    };
  };

  const beginInlineTextEditing = ({
    element = null,
    elementType = 'text',
    at,
    initialValue = '',
  }) => {
    if (!canEdit || !currentPage || !at) return;

    // A second board click applies the previous draft before opening the next
    // one. The ref makes blur + click resolve to one commit only.
    if (inlineEditorRef.current) {
      finishInlineTextEditing('commit');
    }

    const isExisting = Boolean(element?.id);
    const editor = {
      pageId: currentPage.pageId,
      elementId: isExisting ? element.id : '',
      elementType: element?.type || elementType,
      x: Number(element?.x ?? at.x),
      y: Number(element?.y ?? at.y),
      value: normalizeElementText(
        isExisting ? element.text : initialValue,
        element?.type || elementType
      ),
      colour: element?.colour || stroke,
      fontSize: Number(element?.fontSize) || fontSize,
      fontFamily: element?.fontFamily || (elementType === 'equation'
        ? '"Cambria Math", "STIX Two Math", "DejaVu Serif", serif'
        : fontFamily),
      fontWeight: element?.fontWeight || (bold ? 'bold' : 'normal'),
      fontStyle: element?.fontStyle || (italic ? 'italic' : 'normal'),
      textDecoration: element?.textDecoration || (underline ? 'underline' : 'none'),
      fill: element?.fill || fill,
      width: Number(element?.width) || ((element?.type || elementType) === 'sticky' ? 340 : 720),
      height: Number(element?.height) || ((element?.type || elementType) === 'sticky' ? 210 : 220),
    };

    inlineEditorRef.current = editor;
    setInlineEditor(editor);
    setSelectedId(editor.elementId);
    window.requestAnimationFrame(() => {
      const input = inlineTextInputRef.current;
      input?.focus();
      const end = input?.value?.length || 0;
      input?.setSelectionRange?.(end, end);
    });
  };

  const updateInlineText = (value) => {
    const current = inlineEditorRef.current;
    if (!current) return;
    const next = {
      ...current,
      value: normalizeElementText(value, current.elementType),
    };
    inlineEditorRef.current = next;
    setInlineEditor(next);
  };

  const moveInlineTextEditor = ({ x, y }) => {
    const current = inlineEditorRef.current;
    if (!current) return;
    const next = {
      ...current,
      x: clamp(Number(x) || 0, 0, BOARD_WIDTH),
      y: clamp(Number(y) || 0, 0, BOARD_HEIGHT),
    };
    inlineEditorRef.current = next;
    setInlineEditor(next);
  };

  const updateKeyboardTarget = (mode, insertedValue = '') => {
    const inlineTarget = mode === 'inline' && inlineEditorRef.current;
    const input = inlineTarget ? inlineTextInputRef.current : textInputRef.current;
    const value = normalizeBoardText(
      inlineTarget ? inlineEditorRef.current.value : textValue
    );
    const selectionStart = input?.selectionStart ?? value.length;
    const selectionEnd = input?.selectionEnd ?? selectionStart;
    let replaceStart = selectionStart;
    if (insertedValue === '__BACKSPACE__' && selectionStart === selectionEnd) {
      const prefix = Array.from(value.slice(0, selectionStart));
      prefix.pop();
      replaceStart = prefix.join('').length;
    }
    const replacement = insertedValue === '__BACKSPACE__' ? '' : insertedValue;
    const nextPrefix = normalizeBoardText(
      `${value.slice(0, replaceStart)}${replacement}`
    );
    const next = normalizeBoardText(
      `${nextPrefix}${value.slice(selectionEnd)}`
    );
    // NFC composition can reduce code-unit length (for example α + ◌́ → ά).
    // Deriving the caret from the normalized prefix keeps it before trailing
    // text instead of unexpectedly jumping one character to the right.
    const nextCaret = Math.min(next.length, nextPrefix.length);
    if (inlineTarget) updateInlineText(next);
    else setTextValue(next);
    window.requestAnimationFrame(() => {
      const target = inlineTarget ? inlineTextInputRef.current : textInputRef.current;
      target?.focus();
      target?.setSelectionRange?.(nextCaret, nextCaret);
    });
  };

  const ensureInlineKeyboardTarget = () => {
    if (inlineEditorRef.current || !canEdit || !currentPage) return;
    activeTextTargetRef.current = 'inline';
    setTool('text');
    beginInlineTextEditing({
      elementType: 'text',
      at: lastBoardPointRef.current || { x: BOARD_WIDTH / 2, y: BOARD_HEIGHT / 2 },
      initialValue: '',
    });
  };

  const insertKeyboardText = (value) => {
    ensureInlineKeyboardTarget();
    updateKeyboardTarget('inline', value);
  };

  const deleteKeyboardText = () => {
    ensureInlineKeyboardTarget();
    updateKeyboardTarget('inline', '__BACKSPACE__');
  };

  const finishInlineTextEditing = (mode = 'commit') => {
    const draft = inlineEditorRef.current;
    if (!draft) return;

    // Clear first: an unmount-triggered blur cannot save the same draft twice.
    inlineEditorRef.current = null;
    setInlineEditor(null);
    if (mode === 'cancel') return;

    const value = normalizeElementText(draft.value, draft.elementType);
    const page = pagesRef.current.find((item) => item.pageId === draft.pageId);
    if (!page) return;

    if (draft.elementId) {
      const index = page.elements.findIndex((item) => item.id === draft.elementId);
      if (index < 0) return;
      const existing = page.elements[index];
      const nextElements = value.trim()
        ? page.elements.map((item, itemIndex) => itemIndex === index ? {
          ...item,
          text: value,
          x: draft.x,
          y: draft.y,
          width: draft.width,
          height: draft.height,
          direction: getTextDirection(value),
          language: getTextLanguage(value),
        } : item)
        : page.elements.filter((_, itemIndex) => itemIndex !== index);
      if (
        existing.text !== value ||
        Number(existing.x) !== Number(draft.x) ||
        Number(existing.y) !== Number(draft.y) ||
        Number(existing.width || 0) !== Number(draft.width || 0) ||
        Number(existing.height || 0) !== Number(draft.height || 0) ||
        !value.trim()
      ) {
        updatePageElements(draft.pageId, nextElements);
      }
      if (!value.trim()) setSelectedId('');
      return;
    }

    if (!value.trim()) return;
    const element = createTextElement(
      draft.elementType,
      { x: draft.x, y: draft.y },
      value,
      draft
    );
    if (!element) return;
    updatePageElements(draft.pageId, [...page.elements, element]);
    setSelectedId(element.id);
    setTextValue('');
  };
  finishInlineEditorRef.current = finishInlineTextEditing;

  const insertScriptureIntoBoard = useCallback((payload = {}) => {
    const insertionId = String(payload.id || '');
    if (insertionId && handledScriptureIdsRef.current.has(insertionId)) {
      return Promise.resolve({ success: true, duplicate: true, payload });
    }
    const currentPermissions = boardPermissionsRef.current;
    const hasAnnotationAccess = () => (
      boardPermissionsRef.current.canEdit === true ||
      boardPermissionsRef.current.canAnnotate === true
    );
    const canOpenSharedBoard = currentPermissions.canManage === true;
    if (!hasAnnotationAccess() && !canOpenSharedBoard) {
      return Promise.reject(
        new Error('This account does not have Blackboard annotation permission.')
      );
    }
    if (insertionId) handledScriptureIdsRef.current.add(insertionId);

    const releaseInsertion = () => {
      if (insertionId) handledScriptureIdsRef.current.delete(insertionId);
    };
    const refreshAnnotationAccess = async () => {
      if (hasAnnotationAccess()) return;
      const { data } = await api.get(
        `/class-boards/${encodeURIComponent(roomId)}/current`
      );
      if (data?.permissions) applyBoardPermissions(data.permissions);
      if (!hasAnnotationAccess()) {
        throw new Error(
          'The Blackboard opened, but the server did not grant annotation permission.'
        );
      }
    };
    const performInsertion = () => {
      if (!hasAnnotationAccess()) {
        releaseInsertion();
        throw new Error('Blackboard annotation permission was not granted.');
      }
      const page = pagesRef.current.find((item) => item.pageId === activePageIdRef.current)
        || pagesRef.current[0];
      if (!page) {
        releaseInsertion();
        throw new Error('The Blackboard does not have an available page for this passage.');
      }

      const translations = Array.isArray(payload.translations) ? payload.translations : [];
      const fallbackText = [
        payload.reference,
        ...translations.flatMap((translation) => [
          `${translation.shortName || translation.name || translation.id || 'Bible'} — ${translation.language || ''}`.trim(),
          translation.text,
          `${translation.attribution || ''}${translation.license ? ` · ${translation.license}` : ''}`.trim(),
        ]),
      ].filter(Boolean).join('\n');
      const mode = payload.mode === 'sticky' ? 'sticky' : 'text';
      const textChunks = splitScriptureTextForBoard({ payload, fallbackText, mode })
        .filter((value) => value.trim());
      if (!textChunks.length) {
        releaseInsertion();
        throw new Error('The selected Bible passage does not contain insertable text.');
      }

      const elements = textChunks.map((value, partIndex) => {
        const offset = ((page.elements.length + partIndex) % 10) * 32;
        const direction = getTextDirection(value);
        const scriptureFontSize = payload.scope === 'chapter'
          ? mode === 'sticky' ? 18 : 20
          : mode === 'sticky' ? 24 : 28;
        const elementWidth = mode === 'sticky' ? 680 : 780;
        const wrappedLineCount = wrapText(
          value,
          Math.max(8, Math.floor(elementWidth / (scriptureFontSize * 0.58)))
        ).length;
        const contentHeight = clamp(
          wrappedLineCount * scriptureFontSize * 1.2 + (mode === 'sticky' ? 64 : 20),
          mode === 'sticky' ? 260 : 140,
          mode === 'sticky' ? 720 : 820
        );
        const common = {
          id: makeId(),
          type: mode,
          semanticType: 'scripture',
          text: value,
          scripture: {
            reference: String(payload.reference || '').slice(0, 200),
            translationIds: translations.map((item) => String(item.id || item.shortName || '')),
            scope: payload.scope === 'chapter' ? 'chapter' : 'verse',
            partIndex: partIndex + 1,
            partCount: textChunks.length,
          },
          colour: stroke,
          fontSize: scriptureFontSize,
          fontFamily,
          fontWeight: 'normal',
          fontStyle: 'normal',
          textDecoration: 'none',
          direction,
          language: getTextLanguage(value),
          opacity: 1,
          locked: false,
        };
        return mode === 'sticky' ? {
          ...common,
          x: clamp(80 + offset, 0, BOARD_WIDTH - elementWidth),
          y: clamp(70 + offset, 0, BOARD_HEIGHT - contentHeight),
          width: elementWidth,
          height: contentHeight,
          fill,
        } : {
          ...common,
          x: direction === 'rtl' ? BOARD_WIDTH - 100 - offset : 100 + offset,
          y: clamp(
            100 + scriptureFontSize + offset,
            scriptureFontSize,
            BOARD_HEIGHT - contentHeight + scriptureFontSize
          ),
          width: elementWidth,
          height: contentHeight,
        };
      });
      updatePageElements(page.pageId, [...page.elements, ...elements]);
      setSelectedId(elements[0].id);
      setTool('select');
      setVisible(true);
      const outcome = {
        success: true,
        payload,
        elementId: elements[0].id,
        elementIds: elements.map((element) => element.id),
        pageId: page.pageId,
      };
      onScriptureInsertHandled?.(outcome);
      toast.info(
        textChunks.length > 1
          ? `${payload.reference || 'Scripture'} inserted as ${textChunks.length} attributed board parts.`
          : `${payload.reference || 'Scripture'} inserted into the blackboard.`
      );
      return outcome;
    };

    if (visibleRef.current) {
      setMinimized(false);
      return Promise.resolve()
        .then(refreshAnnotationAccess)
        .then(performInsertion)
        .catch((error) => {
          releaseInsertion();
          throw error;
        });
    }
    if (!canOpenSharedBoard) {
      releaseInsertion();
      return Promise.reject(
        new Error('A faculty moderator must open the Blackboard before inserting Scripture.')
      );
    }

    pendingOpenStateRef.current = true;
    if (
      socket?.connected &&
      joinedSocketIdRef.current === String(socket.id || 'connected')
    ) {
      return new Promise((resolve, reject) => {
        let complete = false;
        const acknowledgementTimer = window.setTimeout(() => {
          if (complete) return;
          complete = true;
          pendingOpenStateRef.current = null;
          releaseInsertion();
          reject(new Error('Blackboard opening timed out. Please retry the Bible insertion.'));
        }, 8000);
        socket.emit('board:open', {
          roomId,
          open: true,
          sourceId: sourceIdRef.current,
        }, (result = {}) => {
          if (complete) return;
          complete = true;
          window.clearTimeout(acknowledgementTimer);
          pendingOpenStateRef.current = null;
          if (result.success === false) {
            releaseInsertion();
            reject(new Error(
              result.error || 'Unable to open the Blackboard for Scripture insertion.'
            ));
            return;
          }
          setVisible(true);
          setMinimized(false);
          Promise.resolve()
            .then(refreshAnnotationAccess)
            .then(performInsertion)
            .then(resolve, (error) => {
            releaseInsertion();
            reject(error);
          });
        });
      });
    }

    // Offline/reconnecting: retain exactly one pending open plus the newest
    // snapshot. The join handler emits open before snapshot when reconnected.
    if (!hasAnnotationAccess()) {
      pendingOpenStateRef.current = null;
      releaseInsertion();
      return Promise.reject(
        new Error('Reconnect before opening the Blackboard and inserting Scripture.')
      );
    }
    setVisible(true);
    setMinimized(false);
    return Promise.resolve().then(performInsertion).then((outcome) => ({
      ...outcome,
      queued: true,
    }));
  }, [
    applyBoardPermissions,
    fill,
    fontFamily,
    fontSize,
    onScriptureInsertHandled,
    roomId,
    setVisible,
    socket,
    stroke,
    updatePageElements,
  ]);

  useEffect(() => {
    if (!authorityReady) return undefined;
    return subscribeBlackboardScriptureInsert(insertScriptureIntoBoard);
  }, [authorityReady, insertScriptureIntoBoard]);
  useEffect(() => {
    if (!scriptureInsert || !authorityReady) return;
    void insertScriptureIntoBoard(scriptureInsert).catch((error) => {
      toast.error(error?.message || 'Unable to insert Scripture into the Blackboard.');
    });
  }, [authorityReady, insertScriptureIntoBoard, scriptureInsert]);

  useEffect(() => {
    if (!minimizeRequestId || !visibleRef.current || !canMinimizeLocally) return;
    finishInlineEditorRef.current('commit');
    setMinimized(true);
  }, [canMinimizeLocally, minimizeRequestId]);

  const placeTextAtCentre = (elementType = 'text') => {
    if (!canEdit || !currentPage) return;
    const direction = getTextDirection(textValue);
    beginInlineTextEditing({
      elementType,
      initialValue: textValue,
      at: {
        x: direction === 'rtl' ? BOARD_WIDTH * 0.75 : BOARD_WIDTH * 0.25,
        y: BOARD_HEIGHT * 0.5,
      },
    });
  };

  const pasteTextFromClipboard = async () => {
    if (!navigator.clipboard?.readText) {
      toast.info('Use Ctrl+V or Command+V inside the text box to paste.');
      textInputRef.current?.focus();
      return;
    }

    try {
      const pasted = normalizeBoardText(await navigator.clipboard.readText());
      if (!pasted) {
        toast.info('Clipboard does not contain text.');
        return;
      }
      const input = textInputRef.current;
      const start = input?.selectionStart ?? textValue.length;
      const end = input?.selectionEnd ?? start;
      const next = normalizeBoardText(
        `${textValue.slice(0, start)}${pasted}${textValue.slice(end)}`
      );
      const cursor = Math.min(next.length, start + pasted.length);
      setTextValue(next);
      window.requestAnimationFrame(() => {
        textInputRef.current?.focus();
        textInputRef.current?.setSelectionRange?.(cursor, cursor);
      });
    } catch (error) {
      console.warn('Blackboard clipboard read failed:', error);
      toast.info('Press Ctrl+V or Command+V inside the text box to paste.');
      textInputRef.current?.focus();
    }
  };

  const pointerDown = (event) => {
    if (!svgRef.current) return;
    const pointerType = String(
      event.pointerType || event.nativeEvent?.pointerType || 'mouse'
    ).toLowerCase();
    if (pointerType === 'mouse' && event.button !== 0) return;
    if (pointerType === 'touch' && event.isPrimary === false) {
      event.preventDefault();
      return;
    }
    // Ignore the short palm-contact tail commonly generated immediately
    // after a stylus leaves a touch display. Normal finger drawing is still
    // available whenever no pen has just been active.
    if (pointerType === 'touch' && Date.now() - lastPenInputAtRef.current < 450) {
      event.preventDefault();
      return;
    }
    if (operationRef.current) return;
    if (pointerType === 'pen') lastPenInputAtRef.current = Date.now();
    const at = pointerPoint(event, svgRef.current);
    lastBoardPointRef.current = at;
    publishEphemeral(tool === 'laser' ? 'laser' : 'cursor', at);
    if (!canEdit) return;
    event.preventDefault();

    const targetId = event.target?.closest?.('[data-element-id]')?.getAttribute('data-element-id') || '';
    const resizeHandle = Boolean(event.target?.closest?.('[data-board-resize-handle="true"]'));
    const page = pagesRef.current.find((item) => item.pageId === activePageIdRef.current) || pagesRef.current[0];

    if (tool === 'laser') {
      try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}
      operationRef.current = { mode: 'laser', pointerId: event.pointerId, pointerType };
      return;
    }

    if (tool === 'text' || tool === 'sticky' || tool === 'equation') {
      const target = page.elements.find((item) => item.id === targetId);
      if (target?.locked && ['text', 'sticky'].includes(target.type)) {
        setSelectedId(target.id);
        toast.info('Unlock this text before editing it.');
        return;
      }
      const editableTarget = !target?.locked && ['text', 'sticky'].includes(target?.type)
        ? target
        : null;
      beginInlineTextEditing({
        element: editableTarget,
        elementType: editableTarget?.type || tool,
        at,
        initialValue: editableTarget ? editableTarget.text : textValue,
      });
      return;
    }

    if (tool === 'select') {
      setSelectedId(targetId);
      if (!targetId) return;
      const target = page.elements.find((item) => item.id === targetId);
      if (!target || target.locked) return;
      try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}
      pushHistory(page.elements);
      operationRef.current = resizeHandle
        ? { mode: 'resize', elementId: targetId, start: at, original: deepClone(target), pointerId: event.pointerId, pointerType }
        : { mode: 'drag', elementId: targetId, last: at, pointerId: event.pointerId, pointerType };
      return;
    }
    if (tool === 'eraser') {
      try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}
      pushHistory(page.elements);
      operationRef.current = { mode: 'erase', pointerId: event.pointerId, pointerType };
      eraseAt(at);
      return;
    }
    if (tool === 'icon') {
      const element = {
        id: makeId(),
        type: 'icon',
        x: at.x,
        y: at.y,
        icon: selectedIcon,
        size: Math.max(48, fontSize * 1.6),
        opacity: 1,
        locked: false,
      };
      updateActiveElements([...page.elements, element]);
      setSelectedId(element.id);
      return;
    }

    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}
    pushHistory(page.elements);
    let element;
    if (['pen', 'pencil', 'highlighter'].includes(tool)) {
      element = {
        id: makeId(), type: 'path', points: [{
          ...at,
          pressure: normalizePointerPressure(event.nativeEvent || event),
          pointerType,
        }], stroke,
        strokeWidth: tool === 'highlighter' ? strokeWidth * 4 : tool === 'pencil' ? Math.max(1.5, strokeWidth * 0.8) : strokeWidth,
        opacity: tool === 'highlighter' ? 0.25 : tool === 'pencil' ? 0.92 : 1,
        lineCap: 'round', dash: [], locked: false,
        pressureSensitive: pointerType === 'pen',
      };
      pendingStrokeSamplesRef.current = [];
      operationRef.current = {
        mode: 'path',
        elementId: element.id,
        last: at,
        pointerId: event.pointerId,
        pointerType,
      };
    } else {
      element = {
        id: makeId(), type: tool, stroke, strokeWidth, fill: shapeFilled ? fill : 'transparent',
        dash, opacity: 1, locked: false,
      };
      element = updateShape(element, at, at);
      operationRef.current = {
        mode: 'shape', elementId: element.id, start: at,
        pointerId: event.pointerId, pointerType,
      };
    }
    const next = pagesRef.current.map((item) => item.pageId === page.pageId ? { ...item, elements: [...item.elements, element] } : item);
    setPages(next);
    setSaveState('dirty');
    // A writing stroke is not an explicit selection. Keeping it selected
    // used to make conversion capture just the final letter of a phrase.
    setSelectedId(['pen', 'pencil', 'highlighter'].includes(tool) ? '' : element.id);
  };

  const pointerDoubleClick = (event) => {
    if (!canEdit || !svgRef.current) return;
    const targetId = event.target?.closest?.('[data-element-id]')?.getAttribute('data-element-id') || '';
    const page = pagesRef.current.find((item) => item.pageId === activePageIdRef.current);
    const element = page?.elements?.find((item) => item.id === targetId);
    if (!element || element.locked || !['text', 'sticky'].includes(element.type)) return;
    event.preventDefault();
    event.stopPropagation();
    beginInlineTextEditing({
      element,
      at: { x: element.x, y: element.y },
    });
  };

  const pointerMove = (event) => {
    if (!svgRef.current) return;
    const samples = getCoalescedPointerEvents(event);
    const lastSample = samples[samples.length - 1] || event;
    const at = pointerPoint(lastSample, svgRef.current);
    publishEphemeral(tool === 'laser' ? 'laser' : 'cursor', at);
    if (!canEdit || !operationRef.current) return;
    if (
      operationRef.current.pointerId !== undefined &&
      event.pointerId !== operationRef.current.pointerId
    ) return;
    event.preventDefault();
    const operation = operationRef.current;
    if (operation.pointerType === 'pen') lastPenInputAtRef.current = Date.now();
    if (operation.mode === 'laser') return;
    if (operation.mode === 'erase') {
      samples.forEach((sample) => eraseAt(pointerPoint(sample, svgRef.current)));
      return;
    }
    if (operation.mode === 'path') {
      operation.last = at;
      queueStrokeSamples(samples);
      return;
    }
    const pageId = activePageIdRef.current;
    const next = pagesRef.current.map((page) => {
      if (page.pageId !== pageId) return page;
      return { ...page, elements: page.elements.map((element) => {
        if (element.id !== operation.elementId) return element;
        if (operation.mode === 'drag') {
          const moved = translateElement(element, at.x - operation.last.x, at.y - operation.last.y);
          operation.last = at;
          return moved;
        }
        if (operation.mode === 'resize') {
          return resizeElement(
            operation.original,
            at.x - operation.start.x,
            at.y - operation.start.y
          );
        }
        if (operation.mode === 'shape') return updateShape(element, operation.start, at);
        return element;
      }) };
    });
    setPages(next);
  };

  const pointerUp = (event) => {
    event?.preventDefault?.();
    const operation = operationRef.current;
    if (
      operation?.pointerId !== undefined &&
      event?.pointerId !== operation.pointerId
    ) return;
    if (operation?.pointerType === 'pen') lastPenInputAtRef.current = Date.now();
    if (operation?.mode === 'path') {
      // Short pen/finger strokes may have no pointermove before release.
      // Preserve the real final point while ignoring WebView synthetic (0, 0)
      // cancellation coordinates and lost-pointer-capture events.
      pendingStrokeSamplesRef.current.push(...snapshotPointerSamples(getPointerReleaseSamples(event)));
      flushPendingStrokeSamples();
    }
    if (!operationRef.current) return;
    // Clear first because releasing capture can synchronously dispatch a
    // lostpointercapture event in embedded browsers.
    operationRef.current = null;
    try {
      if (svgRef.current?.hasPointerCapture?.(event.pointerId)) {
        svgRef.current.releasePointerCapture?.(event.pointerId);
      }
    } catch {}
    if (operation.mode === 'laser') return;
    syncBoard(pagesRef.current, activePageIdRef.current);
  };

  pointerEndRef.current = pointerUp;
  useEffect(() => {
    const endOutsideBoard = (event) => {
      if (operationRef.current) pointerEndRef.current?.(event);
    };
    const endOnBlur = () => {
      const operation = operationRef.current;
      if (operation) pointerEndRef.current?.({ type: 'blur', pointerId: operation.pointerId });
    };
    // Pointer capture is missing or unreliable in some tablet WebViews. A
    // release outside the SVG must not leave writing stuck for the next word.
    window.addEventListener('pointerup', endOutsideBoard);
    window.addEventListener('pointercancel', endOutsideBoard);
    window.addEventListener('blur', endOnBlur);
    return () => {
      window.removeEventListener('pointerup', endOutsideBoard);
      window.removeEventListener('pointercancel', endOutsideBoard);
      window.removeEventListener('blur', endOnBlur);
    };
  }, []);

  const undoOnce = useCallback(() => {
    const previous = undoStack[undoStack.length - 1];
    if (!previous || !currentPage) return;
    setRedoStack((items) => [...items, deepClone(currentPage.elements)]);
    setUndoStack((items) => items.slice(0, -1));
    updateActiveElements(previous, { history: false, sync: true });
    setSelectedId('');
  }, [currentPage, undoStack, updateActiveElements]);
  const redoOnce = useCallback(() => {
    const next = redoStack[redoStack.length - 1];
    if (!next || !currentPage) return;
    setUndoStack((items) => [...items, deepClone(currentPage.elements)]);
    setRedoStack((items) => items.slice(0, -1));
    updateActiveElements(next, { history: false, sync: true });
    setSelectedId('');
  }, [currentPage, redoStack, updateActiveElements]);

  useEffect(() => {
    if (!visible || !canEdit) return undefined;
    const keyDown = (event) => {
      const tag = event.target?.tagName?.toLowerCase();
      if (['input', 'textarea', 'select'].includes(tag)) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') { event.preventDefault(); event.shiftKey ? redoOnce() : undoOnce(); }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') { event.preventDefault(); redoOnce(); }
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedId) {
        event.preventDefault(); updateActiveElements(currentPage.elements.filter((item) => item.id !== selectedId)); setSelectedId('');
      }
      const shortcuts = { v: 'select', p: 'pen', b: 'pencil', h: 'highlighter', e: 'eraser', l: 'line', r: 'rect', c: 'ellipse', t: 'text', k: 'laser', q: 'equation' };
      if (!event.ctrlKey && !event.metaKey && shortcuts[event.key.toLowerCase()]) setTool(shortcuts[event.key.toLowerCase()]);
    };
    window.addEventListener('keydown', keyDown);
    return () => window.removeEventListener('keydown', keyDown);
  }, [canEdit, currentPage, redoOnce, selectedId, undoOnce, updateActiveElements, visible]);

  const addPage = () => {
    const page = emptyPage(pagesRef.current.length + 1);
    const next = [...pagesRef.current, page];
    setPages(next); setActivePageId(page.pageId); setUndoStack([]); setRedoStack([]); setSelectedId('');
    syncBoard(next, page.pageId);
  };
  const duplicatePage = () => {
    if (!currentPage) return;
    const page = { ...deepClone(currentPage), pageId: makeId(), name: `${currentPage.name} copy`, elements: currentPage.elements.map((item) => ({ ...item, id: makeId() })) };
    const index = pagesRef.current.findIndex((item) => item.pageId === currentPage.pageId);
    const next = [...pagesRef.current.slice(0, index + 1), page, ...pagesRef.current.slice(index + 1)];
    setPages(next); setActivePageId(page.pageId); syncBoard(next, page.pageId);
  };
  const deletePage = () => {
    if (pagesRef.current.length <= 1) return toast.info('At least one page is required');
    const index = pagesRef.current.findIndex((item) => item.pageId === currentPage.pageId);
    const next = pagesRef.current.filter((item) => item.pageId !== currentPage.pageId);
    const nextPage = next[Math.min(index, next.length - 1)];
    setPages(next); setActivePageId(nextPage.pageId); setSelectedId(''); syncBoard(next, nextPage.pageId);
  };
  const renamePage = () => {
    const name = window.prompt('Page name', currentPage?.name || 'Page');
    if (!name?.trim()) return;
    const next = pagesRef.current.map((item) => item.pageId === currentPage.pageId ? { ...item, name: name.trim().slice(0, 100) } : item);
    setPages(next); syncBoard(next, currentPage.pageId);
  };
  const movePage = (direction) => {
    const index = pagesRef.current.findIndex((item) => item.pageId === currentPage.pageId);
    const target = index + direction;
    if (target < 0 || target >= pagesRef.current.length) return;
    const next = [...pagesRef.current];
    [next[index], next[target]] = [next[target], next[index]];
    setPages(next); syncBoard(next, currentPage.pageId);
  };
  const updatePageBackground = (updates) => {
    const next = pagesRef.current.map((item) => item.pageId === currentPage.pageId ? { ...item, ...updates } : item);
    setPages(next); syncBoard(next, currentPage.pageId);
  };

  const updateSelectedTextLayout = (updates) => {
    if (
      !selectedElement ||
      selectedElement.locked ||
      !['text', 'sticky'].includes(selectedElement.type) ||
      !currentPage
    ) return;
    const elements = currentPage.elements.map((item) => item.id === selectedElement.id
      ? translateElement({ ...item, ...updates }, 0, 0)
      : item);
    updateActiveElements(elements);
  };

  const alterSelected = (action) => {
    if (!selectedElement || !currentPage) return;
    let elements = [...currentPage.elements];
    const index = elements.findIndex((item) => item.id === selectedElement.id);
    pushHistory(elements);
    if (action === 'delete') elements.splice(index, 1);
    if (action === 'duplicate') elements.splice(index + 1, 0, { ...deepClone(selectedElement), id: makeId(), ...(selectedElement.x != null ? { x: selectedElement.x + 30, y: selectedElement.y + 30 } : {}) });
    if (action === 'front') { const [item] = elements.splice(index, 1); elements.push(item); }
    if (action === 'back') { const [item] = elements.splice(index, 1); elements.unshift(item); }
    if (action === 'lock') elements[index] = { ...elements[index], locked: !elements[index].locked };
    updateActiveElements(elements, { history: false, sync: true });
    if (action === 'delete') setSelectedId('');
  };

  const uploadImage = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentPage) return;
    try {
      const result = await compressImage(file);
      const displayWidth = Math.min(800, result.width);
      const displayHeight = displayWidth * (result.height / result.width);
      const element = { id: makeId(), type: 'image', x: (BOARD_WIDTH - displayWidth) / 2, y: (BOARD_HEIGHT - displayHeight) / 2, width: displayWidth, height: displayHeight, src: result.src, alt: file.name, opacity: 1, locked: false };
      updateActiveElements([...currentPage.elements, element]);
      setSelectedId(element.id); setTool('select');
    } catch (error) { toast.error(error.message); }
  };

  const exportPng = async () => {
    if (!svgRef.current) return;
    try {
      const clone = svgRef.current.cloneNode(true);
      clone.setAttribute('width', String(BOARD_WIDTH)); clone.setAttribute('height', String(BOARD_HEIGHT));
      const blob = new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const image = new Image();
      await new Promise((resolve, reject) => { image.onload = resolve; image.onerror = reject; image.src = url; });
      const canvas = document.createElement('canvas'); canvas.width = BOARD_WIDTH; canvas.height = BOARD_HEIGHT;
      canvas.getContext('2d').drawImage(image, 0, 0); URL.revokeObjectURL(url);
      const link = document.createElement('a'); link.download = `${currentPage?.name || 'blackboard'}.png`; link.href = canvas.toDataURL('image/png'); link.click();
    } catch { toast.error('Unable to export this page'); }
  };

  const toggleBoardExpansion = () => {
    // Keep fullscreen ownership with MeetingRoom. Fullscreening only this
    // child hides microphone/camera/chat/leave controls in every browser.
    setViewSize((value) => value === 'full' ? 'large' : 'full');
  };

  flushBoardRef.current = async (options = {}) => {
    const currentAuthority = boardPermissionsRef.current;
    if (!currentAuthority.canEdit && !currentAuthority.canAnnotate && !currentAuthority.canManage &&
        !(options.finalFlush && ['admin', 'faculty', 'staff'].includes(actorRole))) return;
    finishInlineEditorRef.current?.('commit');
    const unfinishedOperation = operationRef.current;
    flushPendingStrokeSamples();
    if (unfinishedOperation) {
      operationRef.current = null;
      syncBoard(pagesRef.current, activePageIdRef.current);
    }
    window.clearTimeout(saveTimerRef.current);
    if (clientRevisionRef.current > lastPersistedRevisionRef.current) {
      await persist(pagesRef.current, activePageIdRef.current, clientRevisionRef.current, options);
    } else await persistQueueRef.current;
  };

  useEffect(() => {
    const flush = (options) => flushBoardRef.current?.(options);
    const unregister = registerClassBoardFlush(roomId, flush);
    const beforeUnload = (event) => {
      if (clientRevisionRef.current <= lastPersistedRevisionRef.current) return;
      void flush({ finalFlush: true }).catch(() => undefined);
      event.preventDefault(); event.returnValue = '';
    };
    const hidden = () => { if (document.visibilityState === 'hidden') void flush({ finalFlush: true }).catch(() => undefined); };
    const pagehide = () => { void flush({ finalFlush: true }).catch(() => undefined); };
    window.addEventListener('beforeunload', beforeUnload);
    window.addEventListener('pagehide', pagehide);
    document.addEventListener('visibilitychange', hidden);
    const retry = window.setInterval(() => {
      if (pendingLiveSnapshotRef.current && navigator.onLine !== false) void flush({ finalFlush: true }).catch(() => undefined);
    }, 15000);
    return () => {
      unregister();
      window.clearInterval(retry);
      window.removeEventListener('beforeunload', beforeUnload);
      window.removeEventListener('pagehide', pagehide);
      document.removeEventListener('visibilitychange', hidden);
      void flush({ finalFlush: true }).catch(() => undefined);
    };
  }, [roomId]);

  const openHandwriting = (scope = 'page') => {
    try {
      finishInlineTextEditing('commit');
      const ink = handwritingSelection(currentPage, selectedId, scope);
      const raster = renderHandwriting(ink);
      setHandwriting({ ...raster, pageId: currentPage.pageId, ink: deepClone(ink), scope, language: handwriting?.language || 'auto', text: '' });
      setRecognitionError('');
    } catch (error) { toast.error(error.message); }
  };
  const recognizeInk = async () => {
    if (!handwriting || recognitionBusy || !canEdit) return;
    setRecognitionBusy(true); setRecognitionError('');
    try {
      const { data } = await api.post(`/class-boards/${encodeURIComponent(roomId)}/handwriting`,
        { image: handwriting.image, language: handwriting.language }, { timeout: 35000 });
      setHandwriting((current) => current ? { ...current, text: normalizeBoardText(data.text) } : null);
    } catch (error) { setRecognitionError(error.response?.data?.error || 'Unable to recognize handwriting. Your ink is unchanged.'); }
    finally { setRecognitionBusy(false); }
  };
  const applyHandwriting = (replaceInk) => {
    if (!canEdit || !handwriting?.text?.trim()) return;
    const page = pagesRef.current.find((item) => item.pageId === handwriting.pageId);
    if (!page) { setRecognitionError('This page was removed. Copy the text before closing.'); return; }
    const inkIds = new Set(handwriting.ink.map((item) => item.id));
    if (replaceInk && handwriting.ink.some((original) => JSON.stringify(page.elements.find((item) => item.id === original.id)) !== JSON.stringify(original))) {
      setRecognitionError('This ink changed during recognition. Use Add text or start recognition again.'); return;
    }
    const value = normalizeBoardText(handwriting.text).trim();
    const rtl = getTextDirection(value) === 'rtl';
    const textWidth = Math.min(BOARD_WIDTH - 80, Math.max(600, handwriting.width));
    let fittedFontSize = Math.min(72, fontSize);
    const measuredHeight = () => wrapText(value, Math.max(6, Math.floor(textWidth / (fittedFontSize * 0.58)))).length * fittedFontSize * 1.2;
    while (fittedFontSize > 16 && measuredHeight() > BOARD_HEIGHT - 80) fittedFontSize -= 2;
    const textHeight = measuredHeight();
    if (textHeight > BOARD_HEIGHT - 80) {
      setRecognitionError('The full text needs more than one page. Shorten the reviewed text or copy it in sections; your handwriting is unchanged.'); return;
    }
    const left = clamp(handwriting.x, 40, BOARD_WIDTH - textWidth - 40);
    const top = clamp(handwriting.y + (replaceInk ? 0 : handwriting.height + 15), 40, BOARD_HEIGHT - textHeight - 40);
    const element = { id: makeId(), type: 'text', text: value,
      x: rtl ? left + textWidth : left,
      y: top + fittedFontSize,
      width: textWidth, height: textHeight,
      colour: stroke, fontSize: fittedFontSize, fontFamily: ORIGINAL_LANGUAGE_FONT,
      direction: getTextDirection(value), language: getTextLanguage(value), opacity: 1,
    };
    updatePageElements(page.pageId, [...page.elements.filter((item) => !replaceInk || !inkIds.has(item.id)), element]);
    setHandwriting(null); setSelectedId(element.id); setTool('select');
  };
  const recoverDraft = () => {
    if (!canEdit || !recoveryDraft?.pages?.length) return;
    if (!window.confirm('Restore the unsaved local board? This replaces the current shared pages.')) return;
    const restoredPages = safePages(recoveryDraft.pages);
    setPages(restoredPages); setActivePageId(recoveryDraft.activePageId || restoredPages[0].pageId);
    syncBoard(restoredPages, recoveryDraft.activePageId || restoredPages[0].pageId, true);
    setRecoveryDraft(null);
  };

  const close = async () => {
    if (!canControlSharedBoard) return;
    finishInlineTextEditing('commit');
    window.clearTimeout(saveTimerRef.current);
    try { await flushBoardRef.current?.(); }
    catch { toast.error('The board has not saved. Retry before closing.'); return; }
    setMinimized(false);
    setVisible(false);
    onClose?.();
    pendingOpenStateRef.current = false;
    if (
      socket?.connected &&
      joinedSocketIdRef.current === String(socket.id || 'connected')
    ) {
      socket.emit('board:open', {
        roomId,
        open: false,
        sourceId: sourceIdRef.current,
      });
      pendingOpenStateRef.current = null;
    }
  };

  const constrainPreviewPosition = useCallback((left, top) => {
    const preview = previewRef.current;
    const width = preview?.offsetWidth || Math.min(380, window.innerWidth * 0.92);
    const height = preview?.offsetHeight || width * 0.7;
    const styles = preview ? window.getComputedStyle(preview) : null;
    const controlsHeight = Number.parseFloat(
      styles?.getPropertyValue('--meeting-controls-height') || '92'
    ) || 92;
    return {
      left: clamp(Number(left) || 8, 8, Math.max(8, window.innerWidth - width - 8)),
      top: clamp(
        Number(top) || 8,
        8,
        Math.max(8, window.innerHeight - controlsHeight - height - 8)
      ),
    };
  }, []);

  const beginPreviewDrag = (event) => {
    if (!canMovePreview || event.target?.closest?.('button') || !previewRef.current) return;
    const rect = previewRef.current.getBoundingClientRect();
    previewDragRef.current = {
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    setPreviewPosition(constrainPreviewPosition(rect.left, rect.top));
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch {}
    event.preventDefault();
  };

  const movePreview = (event) => {
    const drag = previewDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setPreviewPosition(constrainPreviewPosition(
      event.clientX - drag.offsetX,
      event.clientY - drag.offsetY
    ));
    event.preventDefault();
  };

  const endPreviewDrag = (event) => {
    const drag = previewDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    previewDragRef.current = null;
    try { event.currentTarget.releasePointerCapture?.(event.pointerId); } catch {}
  };

  useEffect(() => {
    if (!previewPosition) return undefined;
    const constrain = () => setPreviewPosition((position) => position
      ? constrainPreviewPosition(position.left, position.top)
      : position);
    window.addEventListener('resize', constrain);
    window.addEventListener('orientationchange', constrain);
    return () => {
      window.removeEventListener('resize', constrain);
      window.removeEventListener('orientationchange', constrain);
    };
  }, [constrainPreviewPosition, previewPosition]);

  if (!visible || !currentPage) return null;
  if (minimized) return (
    <>
      {/* Preserve the existing full-size recorder capture target while the
          user sees a small live preview and can use microphone/camera controls. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed"
        style={{ left: -20000, top: 0, width: BOARD_WIDTH, height: BOARD_HEIGHT }}
      >
        <svg
          data-recording-blackboard="true"
          data-board-page-id={currentPage.pageId}
          viewBox={`0 0 ${BOARD_WIDTH} ${BOARD_HEIGHT}`}
          width={BOARD_WIDTH}
          height={BOARD_HEIGHT}
        >
          <BackgroundLayer page={currentPage} />
          {(currentPage.elements || []).map((element) => (
            <BoardElement key={element.id} element={element} selected={false} />
          ))}
        </svg>
      </div>

      <section
        ref={previewRef}
        className="pointer-events-none fixed right-2 z-[110] w-[min(92vw,380px)] overflow-hidden rounded-xl border border-pink-400/80 bg-slate-950 text-white shadow-2xl sm:right-4"
        style={previewPosition ? {
          left: previewPosition.left,
          top: previewPosition.top,
          right: 'auto',
          bottom: 'auto',
        } : { bottom: 'calc(var(--meeting-controls-height, 5.75rem) + 0.5rem)' }}
        aria-label="Minimized live blackboard"
      >
        <header
          className={`pointer-events-auto flex touch-none items-center gap-2 border-b border-slate-700 bg-slate-900 px-3 py-2 ${canMovePreview ? 'cursor-move' : ''}`}
          onPointerDown={beginPreviewDrag}
          onPointerMove={movePreview}
          onPointerUp={endPreviewDrag}
          onPointerCancel={endPreviewDrag}
          onLostPointerCapture={endPreviewDrag}
          title={canMovePreview ? 'Drag to move this preview' : 'Preview position is controlled by the presenter'}
        >
          <FaRegSquare className="text-pink-300" />
          <strong className="min-w-0 flex-1 truncate text-sm">
            Blackboard · {currentPage.name}
          </strong>
          <button
            type="button"
            className="rounded-lg bg-pink-700 px-3 py-1.5 text-xs font-semibold hover:bg-pink-600"
            onClick={() => setMinimized(false)}
            title="Restore blackboard"
          >
            Restore
          </button>
          {canControlSharedBoard && (
            <button
              type="button"
              className="rounded-lg bg-red-700 p-2 text-xs hover:bg-red-600"
              onClick={() => void close()}
              title="Close blackboard for everyone"
              aria-label="Close blackboard for everyone"
            >
              <FaTimes />
            </button>
          )}
        </header>
        <button
          type="button"
          className="pointer-events-auto block w-full bg-slate-800 p-1 text-left"
          onClick={() => setMinimized(false)}
          title="Restore full blackboard"
          aria-label="Restore full blackboard"
        >
          <svg
            viewBox={`0 0 ${BOARD_WIDTH} ${BOARD_HEIGHT}`}
            className="block aspect-video w-full rounded bg-white"
            aria-hidden="true"
          >
            <BackgroundLayer page={currentPage} />
            {(currentPage.elements || []).map((element) => (
              <BoardElement key={element.id} element={element} selected={false} />
            ))}
            <EphemeralPointerLayer pointers={ephemeralPointers} pageId={currentPage.pageId} />
          </svg>
        </button>
        <p className="pointer-events-none bg-slate-900 px-3 py-1.5 text-[11px] text-slate-300">
          Blackboard remains visible. Microphone, camera and meeting controls are available below.
        </p>
      </section>
    </>
  );
  return <div
    className="fixed inset-x-0 top-0 z-[100] flex items-center justify-center bg-slate-950/80 p-0 text-white backdrop-blur-sm sm:p-2"
    style={{ bottom: 'var(--meeting-controls-height, 5.75rem)' }}
    data-meeting-control-safe-overlay="true"
  >
    <section className={`relative flex max-h-full max-w-full flex-col overflow-hidden rounded-xl border border-slate-600 bg-slate-950 shadow-2xl transition-all ${SIZE_CLASSES[viewSize]}`}>
      <header className="z-30 flex min-h-14 flex-wrap items-center gap-2 border-b border-slate-700 bg-slate-900 px-2 py-2 sm:px-3">
        {canEdit && <button className="rounded-lg bg-slate-700 p-2 md:hidden" onClick={() => setSidebarOpen((value) => !value)}><FaBars /></button>}
        <div className="mr-1 min-w-0">
          <strong className="block whitespace-nowrap text-sm sm:text-base">Seminary Teaching Board</strong>
          <span className="block truncate text-[10px] uppercase tracking-wider text-amber-200/80">
            Scripture · Original languages · Live annotation
          </span>
        </div>
        <span className={`rounded-full px-2 py-1 text-[11px] ${saveState === 'error' ? 'bg-red-800' : saveState === 'saved' ? 'bg-emerald-800' : 'bg-amber-700'}`}><FaSave className="mr-1 inline" />{saveState === 'saved' ? 'Saved' : saveState === 'error' ? 'Save failed' : 'Saving…'}</span>
        {canEdit && saveState !== 'saved' && <button className="rounded bg-indigo-700 px-2 py-1 text-xs" onClick={() => void flushBoardRef.current?.().catch(() => undefined)}>Save now</button>}
        <div className="ml-auto flex items-center gap-1">
          {['small', 'medium', 'large', 'full'].map((size) => <button key={size} title={`${size} board`} onClick={() => setViewSize(size)} className={`rounded px-2 py-1 text-xs ${viewSize === size ? 'bg-pink-700' : 'bg-slate-700'}`}>{size[0].toUpperCase()}</button>)}
          <button title={viewSize === 'full' ? 'Restore board size' : 'Expand board without hiding meeting controls'} className="rounded bg-slate-700 p-2" onClick={toggleBoardExpansion}>{viewSize === 'full' ? <FaCompress /> : <FaExpand />}</button>
          {presenterFocusLocked && canMinimizeLocally && <span className="rounded bg-amber-800 px-2 py-1 text-[10px]">Presenter focus</span>}
          {canMinimizeLocally ? (
            <button title="Minimize blackboard and use meeting controls" className="rounded bg-slate-700 p-2" onClick={() => setMinimized(true)}><FaWindowMinimize /></button>
          ) : (
            <span className="rounded bg-amber-800 px-2 py-1 text-[10px] font-semibold" title="Faculty presenter focus keeps this board visible">
              Presenter focus
            </span>
          )}
          {canControlSharedBoard && <button title="Close blackboard for everyone" className="rounded bg-red-700 p-2" onClick={() => void close()}><FaTimes /></button>}
        </div>
      </header>

      {recoveryDraft && <div className="flex flex-wrap items-center gap-2 bg-amber-950 p-2 text-sm">
        <span>Unsaved writing was recovered from this browser.</span>
        {canEdit && <button className="rounded bg-amber-700 px-3 py-1" onClick={recoverDraft}>Restore local board</button>}
        <button className="rounded bg-slate-700 px-3 py-1" onClick={() => {
          const blob = new Blob([JSON.stringify(recoveryDraft, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob); const link = document.createElement('a');
          link.href = url; link.download = `blackboard-recovery-${roomId}.json`; link.click(); URL.revokeObjectURL(url);
        }}>Download recovered writing</button>
      </div>}
      {handwriting && <div role="dialog" aria-label="Handwriting to text" aria-modal="true" className="absolute inset-0 z-50 overflow-y-auto bg-slate-950 p-5">
        <div className="mx-auto max-w-2xl space-y-3">
          <div className="flex justify-between"><h3 className="text-lg font-bold">Handwriting to text</h3><button disabled={recognitionBusy} onClick={() => setHandwriting(null)}>Close</button></div>
          <p className="text-sm text-slate-300">This ink is sent to the configured AI service when you choose Convert. Review spelling and Greek/Hebrew marks before applying. The board stays unchanged until you confirm.</p>
          <label className="block">Capture <select aria-label="Handwriting capture area" className="ml-2 rounded bg-slate-800 p-2" value={handwriting.scope} disabled={recognitionBusy} onChange={(event) => openHandwriting(event.target.value)}>
            <option value="page">All handwriting on this page</option>
            {selectedElement?.type === 'path' && !selectedElement.locked && <option value="selection">Selected stroke only</option>}
          </select></label>
          <p className="text-sm text-slate-300">{handwriting.ink.length} strokes captured. Check that every word appears below before converting.</p>
          <img src={handwriting.image} alt="Handwriting selected for conversion" className="max-h-48 w-full rounded bg-white object-contain" />
          <label className="block">Writing language <select className="ml-2 rounded bg-slate-800 p-2" value={handwriting.language} disabled={recognitionBusy} onChange={(event) => setHandwriting({ ...handwriting, language: event.target.value })}>
            <option value="auto">Auto / mixed languages</option><option value="en">English</option><option value="grc">Greek / Biblical Greek</option><option value="he">Hebrew / Biblical Hebrew</option>
          </select></label>
          <button className="rounded bg-pink-700 px-4 py-2 disabled:opacity-50" disabled={recognitionBusy || !canEdit} onClick={() => void recognizeInk()}>{recognitionBusy ? 'Converting…' : 'Convert handwriting'}</button>
          {recognitionError && <p role="alert" className="text-amber-300">{recognitionError}</p>}
          <label className="block">Review and correct text<textarea aria-label="Recognized handwriting" className="mt-1 min-h-36 w-full rounded bg-slate-800 p-3 text-xl" dir="auto" style={{ fontFamily: ORIGINAL_LANGUAGE_FONT }} value={handwriting.text} onChange={(event) => setHandwriting({ ...handwriting, text: normalizeBoardText(event.target.value) })} /></label>
          <div className="flex gap-3"><button disabled={!handwriting.text.trim() || recognitionBusy || !canEdit} className="rounded bg-emerald-700 px-3 py-2 disabled:opacity-50" onClick={() => applyHandwriting(false)}>Add text, keep ink</button><button disabled={!handwriting.text.trim() || recognitionBusy || !canEdit} className="rounded bg-pink-800 px-3 py-2 disabled:opacity-50" onClick={() => applyHandwriting(true)}>Replace this ink (Undo available)</button></div>
        </div>
      </div>}
      <div className="relative flex min-h-0 flex-1">
        {canEdit && <aside className={`absolute inset-y-0 left-0 z-20 w-[270px] shrink-0 overflow-y-auto border-r border-slate-700 bg-slate-900 p-3 shadow-2xl transition-transform md:relative md:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="mb-3 flex items-center justify-between md:hidden"><strong>Tools</strong><button onClick={() => setSidebarOpen(false)}><FaTimes /></button></div>
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Drawing tools</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              ['select', <FaMousePointer />, 'Select / move (V)'], ['pen', <FaPen />, 'Pen (P)'], ['pencil', <FaPencilAlt />, 'Pencil (B)'],
              ['highlighter', <FaHighlighter />, 'Highlighter (H)'], ['eraser', <FaEraser />, 'Object eraser (E)'], ['line', <FaSlash />, 'Line (L)'],
              ['arrow', <FaLongArrowAltRight />, 'Arrow'], ['rect', <FaRegSquare />, 'Rectangle (R)'], ['ellipse', <FaCircle />, 'Circle (C)'],
              ['triangle', <FaShapes />, 'Triangle'], ['text', <FaFont />, 'Text (T)'], ['sticky', <FaStickyNote />, 'Sticky note'],
              ['laser', <FaMousePointer />, 'Ephemeral laser pointer (K)'], ['equation', <span className="font-serif text-lg">∑</span>, 'Safe Unicode equation (Q)'], ['icon', <FaIcons />, 'Icons'],
            ].map(([name, icon, title]) => <ToolButton key={name} active={tool === name} title={title} onClick={() => setTool(name)}>{icon}</ToolButton>)}
            <ToolButton title="Upload image" onClick={() => imageInputRef.current?.click()}><FaImage /></ToolButton>
            <ReviewFileInput ref={imageInputRef} className="hidden" type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadImage} />
          </div>

          <div className="my-3 border-t border-slate-700" />
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Stroke & shape</p>
          <label className="mb-2 flex items-center justify-between gap-2 text-sm">Line colour <input type="color" value={stroke} onChange={(event) => setStroke(event.target.value)} /></label>
          <label className="mb-2 flex items-center justify-between gap-2 text-sm">Fill colour <input type="color" value={fill} onChange={(event) => setFill(event.target.value)} /></label>
          <label className="mb-1 flex items-center justify-between text-sm">Line size <strong>{strokeWidth}px</strong></label>
          <input className="mb-3 w-full" type="range" min="1" max="60" value={strokeWidth} onChange={(event) => setStrokeWidth(Number(event.target.value))} />
          <select className="mb-2 w-full rounded bg-slate-800 p-2 text-sm" value={lineStyle} onChange={(event) => setLineStyle(event.target.value)}><option value="solid">Solid line</option><option value="dashed">Dashed line</option><option value="dotted">Dotted line</option></select>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={shapeFilled} onChange={(event) => setShapeFilled(event.target.checked)} /> Fill shapes</label>

          <div className="my-3 border-t border-slate-700" />
          <button className="mb-2 w-full rounded-lg bg-indigo-700 px-3 py-2 text-sm" onClick={() => openHandwriting('page')}>Handwriting to text</button>
          <p className="mb-2 text-[11px] text-slate-400">Captures the complete handwriting on this page. You can choose one selected stroke in the preview.</p>
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Text & notes</p>
          <textarea
            ref={textInputRef}
            className="mb-2 min-h-24 w-full resize-y rounded border border-slate-700 bg-slate-800 p-2 text-sm outline-none focus:border-pink-400"
            rows="4"
            value={textValue}
            dir="auto"
            lang={getTextLanguage(textValue)}
            spellCheck={false}
            inputMode="text"
            style={{ fontFamily }}
            onFocus={() => { activeTextTargetRef.current = 'sidebar'; }}
            onSelect={() => { activeTextTargetRef.current = 'sidebar'; }}
            onChange={(event) => setTextValue(normalizeBoardText(event.target.value))}
            onKeyDown={(event) => {
              if (
                event.key === 'Enter' &&
                (event.ctrlKey || event.metaKey) &&
                !event.nativeEvent?.isComposing
              ) {
                event.preventDefault();
                placeTextAtCentre();
              }
            }}
            placeholder="Optional draft: type here, or choose Text and click the board"
          />
          <p className="mb-2 text-[11px] leading-4 text-slate-400">
            Choose Text, click any board point, then type or paste there. Enter adds a line;
            Ctrl/Cmd+Enter applies; Escape cancels; clicking away applies. Greek, Hebrew and
            all Unicode text are supported.
          </p>
          <div className="mb-2 grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => placeTextAtCentre()}
              className="rounded-lg bg-pink-700 px-2 py-2 text-xs font-semibold hover:bg-pink-600"
            >
              Edit on board
            </button>
            <button
              type="button"
              onClick={() => void pasteTextFromClipboard()}
              className="rounded-lg bg-slate-700 px-2 py-2 text-xs font-semibold hover:bg-slate-600"
            >
              Paste
            </button>
            <button
              type="button"
              onClick={() => {
                setTextValue('');
                textInputRef.current?.focus();
              }}
              className="rounded-lg bg-slate-700 px-2 py-2 text-xs font-semibold hover:bg-slate-600"
            >
              Clear
            </button>
          </div>
          <div className="mb-2 grid grid-cols-2 gap-2">
            <select
              className="min-w-0 rounded bg-slate-800 p-2 text-sm"
              value={fontFamily}
              onChange={(event) => setFontFamily(event.target.value)}
              title="Text font"
            >
              {FONT_OPTIONS.map((option) => (
                <option key={option.label} value={option.value}>{option.label}</option>
              ))}
            </select>
            <input
              className="rounded bg-slate-800 p-2 text-sm"
              type="number"
              min="10"
              max="240"
              value={fontSize}
              aria-label="Text size"
              onChange={(event) => setFontSize(clamp(Number(event.target.value), 10, 240))}
            />
          </div>
          <div className="mb-2 grid grid-cols-3 gap-2"><ToolButton active={bold} title="Bold" onClick={() => setBold((value) => !value)}><FaBold /></ToolButton><ToolButton active={italic} title="Italic" onClick={() => setItalic((value) => !value)}><FaItalic /></ToolButton><ToolButton active={underline} title="Underline" onClick={() => setUnderline((value) => !value)}><FaUnderline /></ToolButton></div>
          <div className="mb-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => setKeyboardOpen((value) => !value)}
              className="rounded-lg bg-amber-700 px-2 py-2 text-xs font-semibold hover:bg-amber-600"
            >
              Greek / Hebrew keys
            </button>
            <button
              type="button"
              onClick={() => placeTextAtCentre('equation')}
              className="rounded-lg bg-indigo-700 px-2 py-2 text-xs font-semibold hover:bg-indigo-600"
            >
              Insert equation
            </button>
          </div>
          <div className="mb-2 rounded-lg border border-indigo-400/30 bg-slate-950/50 p-2">
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-indigo-200">Safe Unicode equation palette</p>
            <div className="flex flex-wrap gap-1">
              {['x²', 'xₙ', 'a⁄b', '√x', '∑', '∏', '∞', '≈', '≠', '≤', '≥', '→', '↔', 'α', 'β', 'θ'].map((symbol) => (
                <button
                  key={symbol}
                  type="button"
                  title={`Insert ${symbol} as editable Unicode text`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    activeTextTargetRef.current = 'sidebar';
                    insertKeyboardText(symbol);
                    setTool('equation');
                  }}
                  className="min-w-8 rounded bg-slate-700 px-2 py-1 font-serif text-sm hover:bg-indigo-700"
                >
                  {symbol}
                </button>
              ))}
            </div>
            <p className="mt-1 text-[10px] text-slate-400">Rendered as safe text nodes; no HTML or executable formula markup.</p>
          </div>
          <div className="grid grid-cols-8 gap-1">{ICONS.map((icon) => <button key={icon} onClick={() => { setSelectedIcon(icon); setTool('icon'); }} className={`rounded p-1 text-xl ${selectedIcon === icon ? 'bg-pink-700' : 'bg-slate-800'}`}>{icon}</button>)}</div>

          <div className="my-3 border-t border-slate-700" />
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Page background</p>
          <div className="mb-2 grid grid-cols-9 gap-1">{BACKGROUNDS.map((colour) => <button key={colour} title={colour} onClick={() => updatePageBackground({ background: colour })} className="h-7 rounded border border-slate-500" style={{ background: colour }} />)}</div>
          <select className="w-full rounded bg-slate-800 p-2 text-sm" value={currentPage.backgroundStyle || 'plain'} onChange={(event) => updatePageBackground({ backgroundStyle: event.target.value })}><option value="plain">Plain</option><option value="grid">Grid</option><option value="lines">Ruled lines</option><option value="dots">Dots</option><option value="music">Music staff</option></select>

          <div className="my-3 border-t border-slate-700" />
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-slate-400">Edit</p>
          <div className="grid grid-cols-3 gap-2"><ToolButton title="Undo (Ctrl+Z)" onClick={undoOnce} disabled={!undoStack.length}><FaUndo /></ToolButton><ToolButton title="Redo (Ctrl+Y)" onClick={redoOnce} disabled={!redoStack.length}><FaRedo /></ToolButton><ToolButton title="Clear page" onClick={() => { if (window.confirm('Clear this page?')) updateActiveElements([]); }}><FaTrash /></ToolButton></div>
          {selectedElement && (
            <div className="mt-3 rounded-lg border border-pink-500/50 bg-slate-800 p-2">
              <p className="mb-2 text-xs font-bold text-pink-300">
                Selected: {selectedElement.semanticType === 'scripture' ? 'Scripture ' : ''}{selectedElement.type}
              </p>
              {['text', 'sticky'].includes(selectedElement.type) && !selectedElement.locked && (
                <div className="mb-3 space-y-2 rounded-lg border border-white/10 bg-slate-950/45 p-2">
                  <label className="block text-[11px] text-slate-300">
                    Text size · {Number(selectedElement.fontSize) || 24}px
                    <input
                      aria-label="Selected object text size"
                      className="mt-1 w-full"
                      type="range"
                      min="10"
                      max="120"
                      value={clamp(Number(selectedElement.fontSize) || 24, 10, 120)}
                      onChange={(event) => updateSelectedTextLayout({
                        fontSize: clamp(Number(event.target.value), 10, 120),
                      })}
                    />
                  </label>
                  <label className="block text-[11px] text-slate-300">
                    Object width · {Math.round(Number(selectedElement.width) || 720)}
                    <input
                      aria-label="Selected object width"
                      className="mt-1 w-full"
                      type="range"
                      min="180"
                      max="1400"
                      step="10"
                      value={clamp(Number(selectedElement.width) || 720, 180, 1400)}
                      onChange={(event) => updateSelectedTextLayout({
                        width: clamp(Number(event.target.value), 180, 1400),
                      })}
                    />
                  </label>
                  <label className="block text-[11px] text-slate-300">
                    Object height · {Math.round(Number(selectedElement.height) || 220)}
                    <input
                      aria-label="Selected object height"
                      className="mt-1 w-full"
                      type="range"
                      min="120"
                      max="820"
                      step="10"
                      value={clamp(Number(selectedElement.height) || 220, 120, 820)}
                      onChange={(event) => updateSelectedTextLayout({
                        height: clamp(Number(event.target.value), 120, 820),
                      })}
                    />
                  </label>
                  <select
                    aria-label="Selected object font"
                    className="w-full rounded bg-slate-800 p-2 text-xs"
                    value={selectedElement.fontFamily || ORIGINAL_LANGUAGE_FONT}
                    onChange={(event) => updateSelectedTextLayout({ fontFamily: event.target.value })}
                  >
                    {FONT_OPTIONS.map((option) => (
                      <option key={option.label} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-slate-400">
                    Drag the selected object on the board. Use its pink corner handle for direct resizing.
                  </p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                {['text', 'sticky'].includes(selectedElement.type) && !selectedElement.locked && (
                  <ToolButton
                    title="Edit text"
                    onClick={() => beginInlineTextEditing({
                      element: selectedElement,
                      at: { x: selectedElement.x, y: selectedElement.y },
                    })}
                  >
                    <FaFont />
                  </ToolButton>
                )}
                <ToolButton title="Duplicate" onClick={() => alterSelected('duplicate')}><FaCopy /></ToolButton>
                <ToolButton title="Bring front" onClick={() => alterSelected('front')}><FaArrowUp /></ToolButton>
                <ToolButton title="Send back" onClick={() => alterSelected('back')}><FaArrowDown /></ToolButton>
                <ToolButton title="Lock/unlock" onClick={() => alterSelected('lock')}>{selectedElement.locked ? '🔒' : '🔓'}</ToolButton>
                <ToolButton title="Delete" onClick={() => alterSelected('delete')}><FaTrash /></ToolButton>
              </div>
            </div>
          )}
        </aside>}

        <main className="relative min-w-0 flex-1 overflow-auto bg-slate-800 p-2 sm:p-3">
          {participantDock && <div className="pointer-events-none sticky top-0 z-40 mb-2 ml-auto w-full md:absolute md:right-3 md:top-3 md:w-[30%]">{participantDock}</div>}
          <section
            className={`mb-2 rounded-xl border border-amber-400/30 bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-950 p-2 shadow-lg ${participantDock ? 'md:mr-[31%]' : ''}`}
            aria-label="Professional seminary blackboard toolbar"
            data-seminary-blackboard-toolbar="true"
          >
            <div className="flex flex-wrap items-center gap-2">
              <div className="mr-1 min-w-[170px] flex-1">
                <strong className="block text-xs uppercase tracking-[0.18em] text-amber-200">Teaching workspace</strong>
                <span className="block text-[10px] text-slate-300">
                  {canEdit ? 'Editable live board' : presenterFocusLocked ? 'Faculty presenter focus · view required' : 'Read-only live board'}
                </span>
              </div>
              {canEdit && [
                ['select', 'Select'], ['pen', 'Write'], ['pencil', 'Pencil'], ['text', 'Text'], ['sticky', 'Note'],
                ['equation', 'Equation'], ['laser', 'Laser'],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setTool(id)}
                  className={`rounded-lg border px-2 py-1.5 text-xs font-semibold ${tool === id ? 'border-amber-300 bg-amber-500 text-slate-950' : 'border-white/20 bg-white/10 text-white hover:bg-white/20'}`}
                >
                  {label}
                </button>
              ))}
              {canEdit && <button type="button" className="rounded-lg bg-indigo-700 px-2 py-1.5 text-xs font-semibold" onClick={() => openHandwriting('page')}>Ink → text</button>}
              {canEdit && ['pen', 'pencil'].includes(tool) && (
                <div
                  className="flex items-center gap-1 rounded-lg border border-white/15 bg-slate-950/50 p-1"
                  role="radiogroup"
                  aria-label="Writing thickness"
                >
                  {[
                    [3, 'Fine'],
                    [6, 'Normal'],
                    [10, 'Bold'],
                  ].map(([size, label]) => (
                    <button
                      key={size}
                      type="button"
                      role="radio"
                      aria-checked={strokeWidth === size}
                      aria-label={`${label} writing stroke`}
                      title={`${label} writing stroke`}
                      onClick={() => setStrokeWidth(size)}
                      className={`min-h-8 rounded-md px-2 text-[11px] font-semibold ${strokeWidth === size ? 'bg-cyan-400 text-slate-950' : 'bg-white/10 text-white hover:bg-white/20'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {canEdit && (
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => setKeyboardOpen((value) => !value)}
                  className={`rounded-lg border px-2 py-1.5 text-xs font-semibold ${keyboardOpen ? 'border-cyan-300 bg-cyan-500 text-slate-950' : 'border-white/20 bg-white/10'}`}
                >
                  ΑΩ / אב
                </button>
              )}
              {canManageStudentAnnotations && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={studentAnnotationEnabled}
                  onClick={() => requestStudentAnnotationChange(!studentAnnotationEnabled)}
                  className={`rounded-lg border px-2 py-1.5 text-xs font-semibold ${studentAnnotationEnabled ? 'border-emerald-300 bg-emerald-600' : 'border-slate-500 bg-slate-700'}`}
                >
                  Student annotation: {studentAnnotationEnabled ? 'On' : 'Off'}
                </button>
              )}
              {boardPermissions.canControlPresenterFocus === true && (
                <button
                  type="button"
                  role="switch"
                  aria-checked={liveCollaboration.presenterFocus?.enabled === true}
                  onClick={() => void requestPresenterFocusChange(
                    liveCollaboration.presenterFocus?.enabled !== true
                  )}
                  className={`rounded-lg border px-2 py-1.5 text-xs font-semibold ${liveCollaboration.presenterFocus?.enabled ? 'border-amber-200 bg-amber-600 text-slate-950' : 'border-slate-500 bg-slate-700'}`}
                >
                  Presenter focus: {liveCollaboration.presenterFocus?.enabled ? 'On' : 'Off'}
                </button>
              )}
              {liveCollaboration.presenterFocus?.enabled === true && (
                <span className="rounded-lg border border-amber-300/50 bg-amber-900/70 px-2 py-1.5 text-xs text-amber-100">
                  Presenter focus active
                </span>
              )}
            </div>
          </section>
          {keyboardOpen && canEdit && (
            <SeminaryLanguageKeyboard
              language={keyboardLanguage}
              onLanguageChange={setKeyboardLanguage}
              onInsert={insertKeyboardText}
              onBackspace={deleteKeyboardText}
              onClose={() => setKeyboardOpen(false)}
            />
          )}
          <div className="mb-2 flex items-center justify-center gap-2">
            <button
              type="button"
              aria-label="Zoom out"
              disabled={presenterFocusLocked}
              className="rounded bg-slate-700 p-2 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setZoom((value) => clamp(value - 0.1, 0.35, 2.5))}
            >
              <FaMinus />
            </button>
            <span className="min-w-14 text-center text-xs">{Math.round(zoom * 100)}%</span>
            <button
              type="button"
              aria-label="Zoom in"
              disabled={presenterFocusLocked}
              className="rounded bg-slate-700 p-2 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setZoom((value) => clamp(value + 0.1, 0.35, 2.5))}
            >
              <FaPlus />
            </button>
            <button
              type="button"
              disabled={presenterFocusLocked}
              className="rounded bg-slate-700 px-2 py-2 text-xs disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => setZoom(1)}
            >
              Fit
            </button>
            <button type="button" title="Download current page PNG" className="rounded bg-slate-700 p-2" onClick={() => void exportPng()}><FaDownload /></button>
          </div>
          <div className="mx-auto aspect-video origin-top" style={{ width: `${zoom * 100}%`, minWidth: zoom > 1 ? `${zoom * 100}%` : undefined }}>
            <svg
              ref={svgRef}
              data-recording-blackboard="true"
              data-board-page-id={currentPage.pageId}
              viewBox={`0 0 ${BOARD_WIDTH} ${BOARD_HEIGHT}`}
              className="h-full w-full touch-none select-none rounded-lg shadow-2xl"
              style={{
                cursor: canEdit
                  ? tool === 'select'
                    ? 'default'
                    : tool === 'laser'
                      ? 'none'
                    : tool === 'eraser'
                      ? 'cell'
                      : 'crosshair'
                  : 'default',
                touchAction: 'none',
                overscrollBehavior: 'contain',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTouchCallout: 'none',
              }}
              draggable={false}
              onDragStart={(event) => event.preventDefault()}
              onContextMenu={(event) => {
                if (!event.target?.closest?.('[data-board-inline-editor="true"]')) {
                  event.preventDefault();
                }
              }}
              onPointerDown={pointerDown}
              onPointerMove={pointerMove}
              onPointerUp={pointerUp}
              onPointerCancel={pointerUp}
              onLostPointerCapture={pointerUp}
              onPointerLeave={(event) => {
                if (!operationRef.current) {
                  publishEphemeral(tool === 'laser' ? 'laser' : 'cursor', pointerPoint(event, svgRef.current), false);
                }
              }}
              onDoubleClick={pointerDoubleClick}
            >
              <BackgroundLayer page={currentPage} />
              {(currentPage.elements || []).map((element) => <MemoBoardElement key={element.id} element={element} selected={element.id === selectedId} />)}
              <EphemeralPointerLayer pointers={ephemeralPointers} pageId={currentPage.pageId} />
              {inlineEditor?.pageId === currentPage.pageId && (
                <InlineTextEditor
                  editor={inlineEditor}
                  inputRef={inlineTextInputRef}
                  onChange={updateInlineText}
                  onMove={moveInlineTextEditor}
                  onFinish={finishInlineTextEditing}
                  onActivate={() => { activeTextTargetRef.current = 'inline'; }}
                />
              )}
            </svg>
          </div>
          {!canEdit && <div className="pointer-events-none absolute bottom-4 right-4 max-w-[80%] rounded bg-black/70 px-3 py-2 text-xs">
            {presenterFocusLocked
              ? 'Presenter focus — faculty is sharing this board; minimize and close are disabled.'
              : isStudent && !studentAnnotationEnabled
                ? 'Read only — faculty has paused student annotation.'
                : 'Read only — moderator is presenting.'}
          </div>}
        </main>
      </div>

      <footer className="z-30 flex items-center gap-2 overflow-x-auto border-t border-slate-700 bg-slate-900 p-2">
        {pages.map((page, index) => <button key={page.pageId} disabled={presenterFocusLocked && page.pageId !== currentPage.pageId} onDoubleClick={canEdit && !presenterFocusLocked ? renamePage : undefined} onClick={() => { if (presenterFocusLocked) return; setActivePageId(page.pageId); setSelectedId(''); setUndoStack([]); setRedoStack([]); }} className={`whitespace-nowrap rounded-lg px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40 ${page.pageId === currentPage.pageId ? 'bg-pink-700' : 'bg-slate-700'}`}>{index + 1}. {page.name}</button>)}
        {canEdit && !presenterFocusLocked && <><ToolButton title="Add page" onClick={addPage}><FaPlus /></ToolButton><ToolButton title="Duplicate page" onClick={duplicatePage}><FaCopy /></ToolButton><ToolButton title="Rename page" onClick={renamePage}><FaFont /></ToolButton><ToolButton title="Move page left" onClick={() => movePage(-1)}><FaArrowUp /></ToolButton><ToolButton title="Move page right" onClick={() => movePage(1)}><FaArrowDown /></ToolButton><ToolButton title="Delete page" onClick={deletePage}><FaTrash /></ToolButton></>}
      </footer>
    </section>
  </div>;
};

export default ClassBlackboard;
